import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList
} from 'recharts';
import {
  AlertCircle,
  BarChart3,
  Check,
  ChevronDown,
  Download,
  FileSpreadsheet,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Upload,
  Trash2,
  Calendar,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useJobs } from '../../hooks/useJobs';
import { reportsApi } from '../../lib/api/reportsApi';
import { cn } from '../../lib/utils';
import DateRangePopover from '../../components/ui/DateRangePopover';
import {
  ONBOARDED_LABEL,
  addToFunnel,
  buildSingleFunnelStages,
  emptyFunnelCounts,
  funnelStagesFor,
  hasOnboarding,
  isOnboardedCandidate,
  withOnboardedFunnelStage,
  withOnboardedOutcome,
  ONBOARDED_EXPORT_LABEL,
} from './gocolours/onboarding';

/* ──────────────────────────────────────────────────────────────────
   CONSTANTS
   ────────────────────────────────────────────────────────────── */

const OUTCOME_COLORS = {
  Selected: '#16a34a',
  Onboarded: '#0d9488',
  Dropped: '#f97316',
  Rejected: '#dc2626',
  'On hold': '#d97706',
  Undecided: '#94a3b8',
};

const JOB_COLORS = [
  '#007BFF', '#16a34a', '#d97706', '#dc2626', '#8b5cf6', '#0891b2', '#e11d48', '#4f46e5',
];

const CATEGORY_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316', '#64748b', '#14b8a6'
];

const REGION_LABEL_MAP = {
  N: 'North', S: 'South', E: 'East', W: 'West', C: 'Central',
  NORTH: 'North', SOUTH: 'South', EAST: 'East', WEST: 'West', CENTRAL: 'Central',
};

const CHART_MAX_CATEGORIES = 7;

/* ──────────────────────────────────────────────────────────────────
   DATE HELPERS
   ────────────────────────────────────────────────────────────── */

function toIsoDate(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getDefaultRange(frame) {
  const now = new Date();
  if (frame === 'week' || frame === '7d') {
    const d7 = new Date();
    d7.setDate(now.getDate() - 6);
    return { from: toIsoDate(d7), to: toIsoDate(endOfDay(now)) };
  }
  if (frame === 'month' || frame === '1m') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toIsoDate(first), to: toIsoDate(endOfDay(now)) };
  }
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toIsoDate(first), to: toIsoDate(endOfDay(now)) };
}

function formatDateLabel(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMonthLabel(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatExportDate(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function compactDate(value) {
  if (!value) return '';
  return String(value).slice(0, 10).replace(/-/g, '');
}

function slugFilePart(value, fallback = 'report', maxLength = 44) {
  const cleaned = String(value || fallback)
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength)
    .replace(/-+$/g, '');
  return cleaned || fallback;
}

function getReportFileBaseName(report) {
  const uniqueJobs = new Set((report.rawCandidates || []).map(c => c.jobTitle).filter(Boolean));
  let jobStr = 'report';

  if (uniqueJobs.size > 1) {
    jobStr = `${uniqueJobs.size}-Jobs`;
  } else if (uniqueJobs.size === 1) {
    jobStr = slugFilePart(Array.from(uniqueJobs)[0], 'job', 30);
  } else if (report.meta.jobTitle) {
    jobStr = slugFilePart(report.meta.jobTitle, 'job', 30);
  }

  const from = compactDate(report.meta.from);
  const to = compactDate(report.meta.to);
  const range = from && to ? `${from}-to-${to}` : compactDate(new Date().toISOString());
  return `TalentCrux_Report_${jobStr}_${range}`;
}

/* ──────────────────────────────────────────────────────────────────
   DATA HELPERS
   ────────────────────────────────────────────────────────────── */

function normalizeReportPayload(payload, fallbackContext) {
  if (!payload || typeof payload !== 'object') return null;
  const fallback = {
    meta: {
      jobId: '',
      jobTitle: '',
      jobs: [],
      screeningId: '',
      screeningName: '',
      from: fallbackContext?.from || '',
      to: fallbackContext?.to || '',
      previousLabel: 'previous period',
    },
    summary: {},
    comparison: {},
    outcomeBreakdown: [],
    scoreDistribution: [],
    funnel: [],
    locationBreakdown: [],
    applicationsOverTime: [],
    rawCandidates: [],
  };
  return {
    ...fallback,
    ...payload,
    meta: { ...fallback.meta, ...(payload.meta || {}) },
    summary: { ...fallback.summary, ...(payload.summary || {}) },
    comparison: { ...fallback.comparison, ...(payload.comparison || {}) },
  };
}

function reportMatchesParams(reportData, params) {
  if (!reportData?.meta || !params) return false;
  return (
    JSON.stringify(reportData.meta.jobId) === JSON.stringify(params.jobId) &&
    JSON.stringify(reportData.meta.screeningId) === JSON.stringify(params.screeningId) &&
    String(reportData.meta.from) === String(params.from) &&
    String(reportData.meta.to) === String(params.to)
  );
}

function normalizeRegionLabel(region) {
  const raw = String(region || '').trim().toUpperCase();
  return REGION_LABEL_MAP[raw] || region || '';
}

function formatStoreLabel(storeId, storeName) {
  if (!storeId && !storeName) return '';
  if (!storeName) return String(storeId);

  let cleanName = String(storeName).trim();
  const idStr = String(storeId).trim();

  if (idStr) {
    // Strip trailing occurrences of the store ID with punctuation/spaces (e.g. "Name;1234" -> "Name")
    // Note: escape idStr to be safe in regex, but ids are usually alphanumeric
    const safeIdStr = idStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`[\\s;,-]+${safeIdStr}$`, 'i');
    cleanName = cleanName.replace(regex, '').trim();
  }

  if (cleanName === idStr) return idStr;

  if (idStr) {
    return `${idStr} — ${cleanName}`;
  }
  return cleanName;
}

function toTitleCase(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ──────────────────────────────────────────────────────────────────
   ARM EXPORT HELPERS
   ────────────────────────────────────────────────────────────── */

function getArmName(candidate) {
  return String(candidate.armName || '').trim() || 'Unassigned ARM';
}

function getArmEmail(candidate) {
  return String(candidate.armEmail || '').trim();
}

function buildArmReportRows(candidates) {
  const groups = new Map();
  candidates.forEach((candidate) => {
    const armName = getArmName(candidate);
    const armEmail = getArmEmail(candidate);
    const key = `${armName.toLowerCase()}|${armEmail.toLowerCase()}`;
    if (!groups.has(key)) {
      groups.set(key, { armName, armEmail, candidates: [], stores: new Set() });
    }
    const group = groups.get(key);
    group.candidates.push(candidate);
    if (candidate.storeId) group.stores.add(candidate.storeId);
  });

  const summaryRows = Array.from(groups.values())
    .sort((a, b) => b.candidates.length - a.candidates.length || a.armName.localeCompare(b.armName))
    .map((group) => ({
      'ARM Name': group.armName,
      'ARM Email': group.armEmail,
      'Candidate Count': group.candidates.length,
      'Store Count': group.stores.size,
      Selected: group.candidates.filter((c) => c.outcome === 'Selected').length,
      Rejected: group.candidates.filter((c) => c.outcome === 'Rejected').length,
      Undecided: group.candidates.filter((c) => !['Selected', 'Rejected', 'Dropped'].includes(c.outcome)).length,
      Dropped: group.candidates.filter((c) => c.outcome === 'Dropped').length,
    }));

  const detailRows = Array.from(groups.values())
    .sort((a, b) => a.armName.localeCompare(b.armName))
    .flatMap((group) =>
      group.candidates
        .slice()
        .sort((a, b) => String(a.candidate || '').localeCompare(String(b.candidate || '')))
        .map((candidate) => ({
          'ARM Name': group.armName,
          'ARM Email': group.armEmail,
          Candidate: candidate.candidate,
          Outcome: candidate.outcome,
          'Store Code': candidate.storeId || '',
          'Store Name': candidate.storeName || '',
          'Store Region': candidate.storeRegion || '',
          Location: candidate.location || '',
          'Applied Date': candidate.appliedDate || candidate.appliedAt,
        }))
    );

  return { summaryRows, detailRows };
}

/* ──────────────────────────────────────────────────────────────────
   PER-JOB BREAKDOWN COMPUTATION
   ────────────────────────────────────────────────────────────── */

function buildCategoryDataFromMap(perJobMap, jobs, sort = true) {
  const allNames = new Set();
  Object.values(perJobMap).forEach((map) => {
    Object.keys(map).forEach((name) => allNames.add(name));
  });
  const data = Array.from(allNames)
    .map((name) => {
      const total = Object.values(perJobMap).reduce((sum, map) => sum + (map[name] || 0), 0);
      const byColumn = {};
      jobs.forEach((job) => {
        byColumn[job.id] = perJobMap[job.id]?.[name] || 0;
      });
      return { name, count: total, byColumn };
    });

  if (sort) {
    data.sort((a, b) => b.count - a.count);
  }
  return data;
}

function computePerJobBreakdowns(rawCandidates, jobs) {
  if (!rawCandidates?.length || !jobs?.length) return null;

  const outcomes = {};
  const funnel = {};
  const byRegion = {};
  const byCity = {};
  const byState = {};
  const byArm = {};
  const byStore = {};
  const timeline = {};

  jobs.forEach((job) => {
    outcomes[job.id] = { Selected: 0, Rejected: 0, Dropped: 0, Undecided: 0 };
    funnel[job.id] = emptyFunnelCounts();
    byRegion[job.id] = { North: 0, South: 0, East: 0, West: 0, Central: 0 };
    byCity[job.id] = {};
    byState[job.id] = {};
    byArm[job.id] = {};
    byStore[job.id] = {};
    timeline[job.id] = {};
  });

  rawCandidates.forEach((c) => {
    const jid = c.jobId;
    if (!jid || !outcomes[jid]) return;

    const outcome = c.outcome || 'Undecided';
    if (outcomes[jid][outcome] !== undefined) outcomes[jid][outcome] += 1;
    else outcomes[jid].Undecided += 1;

    addToFunnel(funnel[jid], c);

    const region = normalizeRegionLabel(c.storeRegion);
    const city = toTitleCase(c.storeCity);
    const state = toTitleCase(c.storeState);
    const arm = c.armName ? toTitleCase(c.armName) : '';
    const store = formatStoreLabel(c.storeId, c.storeName);

    if (region) byRegion[jid][region] = (byRegion[jid][region] || 0) + 1;
    if (city) byCity[jid][city] = (byCity[jid][city] || 0) + 1;
    if (state) byState[jid][state] = (byState[jid][state] || 0) + 1;
    if (arm) byArm[jid][arm] = (byArm[jid][arm] || 0) + 1;
    if (store) byStore[jid][store] = (byStore[jid][store] || 0) + 1;

    const date = c.appliedDate;
    if (date) timeline[jid][date] = (timeline[jid][date] || 0) + 1;
  });

  return {
    outcomes,
    funnel,
    categories: {
      region: buildCategoryDataFromMap(byRegion, jobs),
      city: buildCategoryDataFromMap(byCity, jobs),
      state: buildCategoryDataFromMap(byState, jobs),
      arm: buildCategoryDataFromMap(byArm, jobs),
      store: buildCategoryDataFromMap(byStore, jobs),
    },
    timeline,
  };
}

function getMonthsInRange(fromStr, toStr) {
  if (!fromStr || !toStr) return [];
  const start = new Date(fromStr);
  const end = new Date(toStr);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const months = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (current <= lastMonth) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, '0');
    const monthId = `${yyyy}-${mm}`;
    const monthTitle = current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    months.push({ id: monthId, title: monthTitle });
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}

function computePerMonthBreakdowns(rawCandidates, months) {
  if (!rawCandidates?.length || !months?.length) return null;

  const outcomes = {};
  const funnel = {};
  const byRegion = {};
  const byCity = {};
  const byState = {};
  const byArm = {};
  const byStore = {};

  months.forEach((m) => {
    outcomes[m.id] = { Selected: 0, Rejected: 0, Dropped: 0, Undecided: 0 };
    funnel[m.id] = emptyFunnelCounts();
    byRegion[m.id] = { North: 0, South: 0, East: 0, West: 0, Central: 0 };
    byCity[m.id] = {};
    byState[m.id] = {};
    byArm[m.id] = {};
    byStore[m.id] = {};
  });

  rawCandidates.forEach((c) => {
    const dateVal = c.appliedDate || c.appliedAt;
    if (!dateVal) return;
    const monthId = String(dateVal).slice(0, 7);
    if (!outcomes[monthId]) return;

    // Outcomes
    const outcome = c.outcome || 'Undecided';
    if (outcomes[monthId][outcome] !== undefined) {
      outcomes[monthId][outcome] += 1;
    } else {
      outcomes[monthId].Undecided += 1;
    }

    // Funnel
    addToFunnel(funnel[monthId], c);

    // Categories
    const region = normalizeRegionLabel(c.storeRegion);
    const city = toTitleCase(c.storeCity);
    const state = toTitleCase(c.storeState);
    const arm = c.armName ? toTitleCase(c.armName) : '';
    const store = c.storeId
      ? `${c.storeId}${c.storeName ? ` — ${c.storeName}` : ''}`
      : '';

    if (region) byRegion[monthId][region] = (byRegion[monthId][region] || 0) + 1;
    if (city) byCity[monthId][city] = (byCity[monthId][city] || 0) + 1;
    if (state) byState[monthId][state] = (byState[monthId][state] || 0) + 1;
    if (arm) byArm[monthId][arm] = (byArm[monthId][arm] || 0) + 1;
    if (store) byStore[monthId][store] = (byStore[monthId][store] || 0) + 1;
  });

  return {
    outcomes,
    funnel,
    categories: {
      region: buildCategoryDataFromMap(byRegion, months),
      city: buildCategoryDataFromMap(byCity, months),
      state: buildCategoryDataFromMap(byState, months),
      arm: buildCategoryDataFromMap(byArm, months),
      store: buildCategoryDataFromMap(byStore, months),
    },
  };
}

/** Build single-job category data from rawCandidates for a given field extractor. */
function buildSingleJobCategory(rawCandidates, extractFn) {
  const counts = new Map();
  rawCandidates.forEach((c) => {
    const value = extractFn(c);
    if (value) counts.set(value, (counts.get(value) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

function buildSingleJobRegionCategory(rawCandidates) {
  const counts = new Map([['North', 0], ['South', 0], ['East', 0], ['West', 0], ['Central', 0]]);
  rawCandidates.forEach((c) => {
    const value = normalizeRegionLabel(c.storeRegion);
    if (value && counts.has(value)) {
      counts.set(value, counts.get(value) + 1);
    }
  });
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

/* ──────────────────────────────────────────────────────────────────
   SHARED UI COMPONENTS
   ────────────────────────────────────────────────────────────── */

function Panel({ title, eyebrow, children, className, headerRight }) {
  return (
    <section className={cn('min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md h-full flex flex-col', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="text-base font-bold leading-snug text-slate-950">{title}</h2>
          {eyebrow && <span className="shrink-0 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">{eyebrow}</span>}
        </div>
        {headerRight && <div className="ml-auto shrink-0">{headerRight}</div>}
      </div>
      <div className="flex-1 min-h-0 min-w-0">{children}</div>
    </section>
  );
}

function ViewToggle({ isActive, onOpenVisual }) {
  return (
    <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/50">
      <button
        type="button"
        onClick={() => { }}
        className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', !isActive ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700 cursor-default')}
      >
        Data
      </button>
      <button
        type="button"
        onClick={onOpenVisual}
        className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', isActive ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700 cursor-pointer')}
      >
        Visual
      </button>
    </div>
  );
}

function SummaryStrip({ summary }) {
  const items = [
    { color: null, value: summary.totalCandidates, label: 'Total' },
    { color: OUTCOME_COLORS.Selected, value: summary.selected, label: `Selected (${summary.selectionRate}%)` },
    ...(Number.isFinite(summary.onboarded)
      ? [{ color: OUTCOME_COLORS.Onboarded, value: summary.onboarded, label: `${ONBOARDED_LABEL} (of selected)` }]
      : []),
    { color: OUTCOME_COLORS.Dropped, value: summary.dropped || 0, label: 'Dropped' },
    { color: OUTCOME_COLORS.Rejected, value: summary.rejected, label: 'Rejected' },
    { color: OUTCOME_COLORS.Undecided, value: summary.undecided ?? (summary.totalCandidates - summary.selected - summary.rejected), label: 'Undecided' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-100 pb-4 mb-4 shrink-0">
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && <div className="hidden sm:block h-5 w-px bg-slate-200" />}
          <div className="flex items-center gap-1.5">
            {item.color && <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />}
            <span className={cn('font-black tabular-nums', i === 0 ? 'text-2xl text-slate-950' : 'text-lg text-slate-950')}>{item.value}</span>
            <span className="text-xs text-slate-500">{item.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function EmptyReportState({ isLoading }) {
  return (
    <section className="flex min-h-[460px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
      <div className="max-w-md">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#007BFF]">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <BarChart3 className="h-5 w-5" />}
        </div>
        <h2 className="mt-4 text-base font-black text-slate-950">{isLoading ? 'Generating report' : 'Please select a job and a screening to generate a report.'}</h2>
        {isLoading && <p className="mt-2 text-sm text-slate-500">We are preparing the hiring report for the selected filters.</p>}
      </div>
    </section>
  );
}

function ErrorReportState({ message, onRetry, isRetrying }) {
  return (
    <section className="flex min-h-[460px] items-center justify-center rounded-2xl border border-dashed border-red-300 bg-white px-6 py-16 text-center shadow-sm">
      <div className="max-w-md">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600">
          <AlertCircle className="h-5 w-5" />
        </div>
        <h2 className="mt-4 text-base font-black text-slate-950">We could not generate this report.</h2>
        <p className="mt-2 text-sm text-slate-500">{message || 'Something went wrong while loading the report.'}</p>
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying}
          className="mt-5 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#007BFF] px-4 text-sm font-bold text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {isRetrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Try again
        </button>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
   OUTCOME PANEL (DATA ONLY)
   ────────────────────────────────────────────────────────────── */

function OutcomeComparisonTable({ columns, perColumnOutcomes, perColumnFunnel, showOnboarded }) {
  const outcomeKeys = ['Selected', 'Rejected', 'Dropped', 'Undecided'];
  const cols = columns || [];
  const outcomes = perColumnOutcomes || {};
  return (
    <div className="overflow-x-auto minimal-scrollbar">
      <table className="report-sticky-first min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2.5 text-left">Outcome</th>
            {cols.map((col) => (
              <th key={col.id} className="px-3 py-2.5 text-center min-w-[96px] max-w-[180px] leading-snug">{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {outcomeKeys.map((outcome) => (
            <React.Fragment key={outcome}>
              <tr className="border-b border-slate-100 transition-colors hover:bg-slate-50/50">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: OUTCOME_COLORS[outcome] || '#94a3b8' }} />
                    <span className="font-medium text-slate-700">{outcome}</span>
                  </div>
                </td>
                {cols.map((col) => (
                  <td key={col.id} className="px-3 py-2.5 text-center font-semibold text-slate-950 tabular-nums">{outcomes[col.id]?.[outcome] || 0}</td>
                ))}
              </tr>
              {/* Part of Selected above, so it is not summed into Total. */}
              {outcome === 'Selected' && showOnboarded && (
                <tr className="border-b border-slate-100 bg-teal-50/40">
                  <td className="py-2 pl-7 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: OUTCOME_COLORS.Onboarded }} />
                      <span className="whitespace-nowrap font-medium text-slate-700">{ONBOARDED_LABEL}</span>
                      <span className="whitespace-nowrap text-[11px] text-slate-400">of selected</span>
                    </div>
                  </td>
                  {cols.map((col) => (
                    <td key={col.id} className="px-3 py-2 text-center font-semibold text-teal-700 tabular-nums">{perColumnFunnel?.[col.id]?.onboarded || 0}</td>
                  ))}
                </tr>
              )}
            </React.Fragment>
          ))}
          <tr className="border-t-2 border-slate-200 bg-slate-50/60">
            <td className="px-3 py-2.5 font-bold text-slate-950">Total</td>
            {cols.map((col) => {
              const total = outcomeKeys.reduce((sum, o) => sum + (outcomes[col.id]?.[o] || 0), 0);
              return <td key={col.id} className="px-3 py-2.5 text-center font-black text-slate-950 tabular-nums">{total}</td>;
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function OutcomePanel({ report, isMultiColumnMode, columns, breakdownOutcomes, breakdownFunnel, activeModal, setActiveModal }) {
  const cols = columns || [];
  const showMulti = isMultiColumnMode && cols.length > 0;
  const showOnboarded = hasOnboarding(report);
  return (
    <Panel
      title="Outcome breakdown"
      eyebrow={showMulti ? `${cols.length} columns` : 'by recruiter decision'}
      className="lg:col-span-6"
      headerRight={<ViewToggle isActive={activeModal === 'outcome'} onOpenVisual={() => setActiveModal('outcome')} />}
    >
      <SummaryStrip summary={report.summary} />

      {showMulti && breakdownOutcomes ? (
        <OutcomeComparisonTable
          columns={cols}
          perColumnOutcomes={breakdownOutcomes}
          perColumnFunnel={breakdownFunnel}
          showOnboarded={showOnboarded}
        />
      ) : (
        <div className="mt-4">
          <div className="flex h-8 w-full overflow-hidden rounded-lg bg-slate-100">
            {report.outcomeBreakdown.map((item) => (
              <div key={item.name} className="flex items-center justify-center text-xs font-bold text-white transition-all" style={{ width: `${Math.max(item.pct, 2)}%`, backgroundColor: OUTCOME_COLORS[item.name] || '#94a3b8' }}>
                {item.pct >= 10 ? `${item.pct}%` : ''}
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2.5">
            {report.outcomeBreakdown.map((item) => (
              <React.Fragment key={item.name}>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2.5">
                    <span className="h-3 w-3 rounded" style={{ backgroundColor: OUTCOME_COLORS[item.name] || '#94a3b8' }} />
                    <span className="font-medium text-slate-700">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <strong className="text-slate-950 tabular-nums">{item.value}</strong>
                    <span className="w-12 text-right text-xs text-slate-400">{item.pct}%</span>
                  </div>
                </div>
                {item.name === 'Selected' && showOnboarded && (
                  <div className="flex items-center justify-between pl-5 text-sm">
                    <div className="flex items-center gap-2.5">
                      <span className="h-3 w-3 rounded" style={{ backgroundColor: OUTCOME_COLORS.Onboarded }} />
                      <span className="whitespace-nowrap font-medium text-slate-700">{ONBOARDED_LABEL}</span>
                      <span className="whitespace-nowrap text-[11px] text-slate-400">of selected</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <strong className="text-teal-700 tabular-nums">{report.summary.onboarded}</strong>
                      <span className="w-12" />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ──────────────────────────────────────────────────────────────────
   FUNNEL PANEL (DATA ONLY)
   ────────────────────────────────────────────────────────────── */

function FunnelPanel({ report, isMultiColumnMode, columns, breakdownFunnel, activeModal, setActiveModal }) {
  const cols = columns || [];
  const showMulti = isMultiColumnMode && cols.length > 0;
  const funnelMap = breakdownFunnel || {};

  const singleStages = useMemo(() => buildSingleFunnelStages(report), [report]);
  const stageHighlight = { selected: 'bg-green-50/40', onboarded: 'bg-teal-50/40' };

  return (
    <Panel
      title="Hiring funnel"
      eyebrow={showMulti ? `${cols.length} columns` : 'stage progression'}
      className="lg:col-span-6"
      headerRight={<ViewToggle isActive={activeModal === 'funnel'} onOpenVisual={() => setActiveModal('funnel')} />}
    >
      <div className="h-full flex flex-col justify-center">
        {showMulti ? (
          <div className="overflow-x-auto minimal-scrollbar">
            <table className="report-sticky-first min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2.5 text-left">Stage</th>
                  {cols.map((col) => (
                    <th key={col.id} className="px-3 py-2.5 text-center min-w-[96px] max-w-[180px] leading-snug">{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {funnelStagesFor(report).map((stage) => (
                  <tr key={stage.key} className={cn('border-b border-slate-100 transition-colors hover:bg-slate-50/50', stageHighlight[stage.key])}>
                    <td className="px-3 py-2.5 font-medium text-slate-700">{stage.label}</td>
                    {cols.map((col) => (
                      <td key={col.id} className="px-3 py-2.5 text-center font-semibold text-slate-950 tabular-nums">{funnelMap[col.id]?.[stage.key] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-4">
            {singleStages.map((item) => (
              <div key={item.key} className={cn('flex items-center justify-between rounded-lg px-4 py-3', item.key === 'selected' ? 'bg-green-50' : item.key === 'onboarded' ? 'bg-teal-50' : 'bg-slate-50')}>
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
                <strong className="text-xl font-black text-slate-950 tabular-nums">{item.count}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

function CategoryPanel({ title, data, isMultiColumnMode, columns, panelId, activeModal, setActiveModal }) {
  if (!data || !data.length) return null;
  const cols = columns || [];
  const showMulti = isMultiColumnMode && cols.length > 0;
  const firstColHeader = title.replace(/^(Selected )?Candidates (by|per) /i, '').trim();

  return (
    <Panel
      title={title}
      eyebrow={`${data.length} entries`}
      headerRight={setActiveModal ? <ViewToggle isActive={activeModal === panelId} onOpenVisual={() => setActiveModal(panelId)} /> : null}
    >
      {!showMulti ? (
        <div className="space-y-1 max-h-[300px] overflow-y-auto minimal-scrollbar pr-1 relative">
          {data.map((item, i) => (
            <div key={item.name} className={cn('flex items-center justify-between rounded-md px-3 py-2 text-sm', i % 2 === 0 ? 'bg-slate-50/60' : '')}>
              <span className="truncate font-medium text-slate-700 mr-4">{item.name || '—'}</span>
              <strong className="shrink-0 text-slate-950 tabular-nums">{item.count}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-h-[300px] overflow-auto minimal-scrollbar relative">
          <table className="min-w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                <th className="sticky top-0 left-0 z-30 bg-white px-3 py-2 text-left shadow-[1px_1px_0_0_#e2e8f0]">{firstColHeader}</th>
                {cols.map((col) => (
                  <th key={col.id} className="sticky top-0 z-20 bg-white px-3 py-2 text-center min-w-[96px] max-w-[160px] leading-snug shadow-[0_1px_0_0_#e2e8f0]">{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, ri) => (
                <tr key={item.name} className={cn('group transition-colors hover:bg-slate-50/50', ri % 2 === 0 ? 'bg-slate-50/30' : '')}>
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-3 py-2 font-medium text-slate-700 truncate max-w-[160px] border-b border-slate-50 shadow-[1px_0_0_0_#e2e8f0]">{item.name || '—'}</td>
                  {cols.map((col) => (
                    <td key={col.id} className="px-3 py-2 text-center font-semibold text-slate-950 tabular-nums border-b border-slate-50">{item.byColumn?.[col.id] || 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

  /* ──────────────────────────────────────────────────────────────────
     VISUAL MODAL & CHART COMPONENTS
     ────────────────────────────────────────────────────────────── */

  function CustomTabularLegend({ payload, total }) {
    if (!payload || !payload.length) return null;
    return (
      <div className="w-full flex justify-center">
        <table className="text-sm text-slate-700 w-full max-w-sm">
          <tbody>
            {payload.map((entry, index) => {
              const count = entry.payload?.value || entry.payload?.count || entry.payload?.payload?.value || entry.payload?.payload?.count || 0;
              const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return (
                <tr key={`legend-row-${index}`} className="border-b border-slate-100 last:border-none">
                  <td className="py-2 pl-2 w-8"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} /></td>
                  <td className="py-2 font-medium">{entry.value}</td>
                  <td className="py-2 text-right font-bold tabular-nums">{count}</td>
                  <td className="py-2 pr-2 text-right text-slate-500 tabular-nums w-20">({pct}%)</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-slate-200">
            <tr>
              <td colSpan={2} className="py-3 pl-2 font-bold text-slate-900">Total</td>
              <td className="py-3 text-right font-bold text-slate-900 tabular-nums">{total}</td>
              <td className="py-3 pr-2 text-right text-slate-500 tabular-nums">(100%)</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  function DonutChartLayout({ data, dataKey, nameKey, colors, title }) {
    const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

    return (
      <div className="flex flex-col h-full w-full pt-4">
        {title && <h4 className="text-lg font-bold text-slate-900 mb-6 truncate w-full text-center px-4">{title}</h4>}

        <div className="flex flex-col lg:flex-row w-full h-full items-center justify-center gap-6 lg:gap-10">

          <div className="relative w-full lg:w-1/2 h-[260px] lg:h-[350px] flex-shrink-0 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" innerRadius="55%" outerRadius="85%" paddingAngle={2} isAnimationActive={true}>
                  {data.map((entry, i) => (
                    <Cell key={entry[nameKey]} fill={colors[entry[nameKey]] || colors[i % (colors.length || 1)] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(value, name) => [`${value}`, name]} />
              </PieChart>
            </ResponsiveContainer>

            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-sm font-semibold text-slate-500">Total</span>
              <span className="text-3xl font-black text-slate-900 leading-tight">{total}</span>
              <span className="text-xs font-medium text-slate-500">Candidates</span>
            </div>
          </div>

          <div className="w-full lg:w-1/2 flex items-center justify-center">
            <CustomTabularLegend
              payload={data.map((d, i) => ({
                color: colors[d[nameKey]] || colors[i % (colors.length || 1)] || '#94a3b8',
                value: d[nameKey],
                payload: { value: d[dataKey] }
              }))}
              total={total}
            />
          </div>
        </div>
      </div>
    );
  }

  function OutcomeVisualSingle({ outcomeBreakdown }) {
    return <DonutChartLayout data={outcomeBreakdown} dataKey="value" nameKey="name" colors={OUTCOME_COLORS} />;
  }

  function FunnelVisualSingle({ stages }) {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={stages} margin={{ top: 20, right: 30, left: 20, bottom: 20 }} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf2f7" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 14, fill: '#475569' }} axisLine={false} tickLine={false} width={120} />
          <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
          <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} isAnimationActive={true} barSize={48} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  function FunnelVisualMulti({ columns, perColumnFunnel, stages }) {
    const chartData = stages.map(({ key, label }) => {
      const datum = { stage: label };
      let total = 0;
      columns.forEach(col => {
        const count = perColumnFunnel[col.id]?.[key] || 0;
        datum[col.title] = count;
        total += count;
      });
      datum.total = total;
      return datum;
    });

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 20 }} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf2f7" />
          <XAxis type="number" tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="stage" tick={{ fontSize: 14, fill: '#475569', fontWeight: 600 }} axisLine={false} tickLine={false} width={120} />
          <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
          <Legend verticalAlign="top" height={60} iconType="square" wrapperStyle={{ fontSize: 14, paddingBottom: 20 }} />
          {columns.map((col, i) => (
            <Bar key={col.id} dataKey={col.title} stackId="a" fill={JOB_COLORS[i % JOB_COLORS.length]} isAnimationActive={true} barSize={48}>
              <LabelList dataKey={col.title} position="inside" fill="#fff" fontSize={12} formatter={(val) => val > 0 ? val : ''} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  function CategoryVisualSingle({ data }) {
    const topN = data.slice(0, CHART_MAX_CATEGORIES);
    const others = data.slice(CHART_MAX_CATEGORIES);
    const chartData = [...topN];
    if (others.length > 0) {
      chartData.push({ name: `Other (${others.length})`, count: others.reduce((sum, item) => sum + item.count, 0) });
    }

    return <DonutChartLayout data={chartData} dataKey="count" nameKey="name" colors={CATEGORY_COLORS} />;
  }

  function CategoryVisualMulti({ data, columns }) {
    const topN = data.slice(0, CHART_MAX_CATEGORIES);
    const others = data.slice(CHART_MAX_CATEGORIES);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full h-full overflow-y-auto minimal-scrollbar p-4 content-start">
        {columns.map((col) => {
          const chartData = [...topN].map(cat => ({ name: cat.name, count: cat.byColumn?.[col.id] || 0 }));
          chartData.sort((a, b) => b.count - a.count);
          if (others.length > 0) {
            chartData.push({ name: `Other (${others.length})`, count: others.reduce((sum, cat) => sum + (cat.byColumn?.[col.id] || 0), 0) });
          }
          const filteredData = chartData.filter(d => d.count > 0);

          return (
            <div key={col.id} className="flex min-h-[400px] flex-col border border-slate-100 rounded-xl shadow-sm bg-slate-50/50 p-4">
              <h4 className="mb-1 w-full truncate text-center text-base font-bold text-slate-900">{col.title}</h4>
              <div className="min-h-0 flex-1">
                <DonutChartLayout data={filteredData} dataKey="count" nameKey="name" colors={CATEGORY_COLORS} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function VisualModal({ activeModal, onClose, report, isMultiColumnMode, columns, breakdownData, categoryData, categoryTitlePrefix = 'Candidates' }) {
    if (!activeModal || !report) return null;

    const cols = columns || [];
    let title = '';
    let content = null;

    if (activeModal === 'outcome') {
      title = 'Outcome Breakdown';
      content = <OutcomeVisualSingle outcomeBreakdown={report.outcomeBreakdown} />;
    } else if (activeModal === 'funnel') {
      title = 'Hiring Funnel';
      const singleStages = buildSingleFunnelStages(report);
      content = isMultiColumnMode
        ? <FunnelVisualMulti columns={cols} perColumnFunnel={breakdownData?.funnel || {}} stages={funnelStagesFor(report)} />
        : <FunnelVisualSingle stages={singleStages} />;
    } else {
      const catMap = { state: 'State', city: 'City', arm: 'ARM', store: 'Store', region: 'Region' };
      title = `${categoryTitlePrefix} by ${catMap[activeModal]}`;
      const data = categoryData?.[activeModal] || [];
      content = isMultiColumnMode
        ? <CategoryVisualMulti data={data} columns={cols} />
        : <CategoryVisualSingle data={data} />;
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-100 bg-slate-50">
            <h3 className="text-lg font-black tracking-tight text-slate-900">{title} — Detailed Visualization</h3>
            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-3 sm:p-6 bg-white w-full h-[70vh] sm:h-[600px] overflow-y-auto">
            {content}
          </div>
        </div>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     TIMELINE PANEL
     ────────────────────────────────────────────────────────────── */

  function TimelinePanel({ report, isMultiJob, perJobData }) {
    const [chartType, setChartType] = useState('area'); // 'area' or 'bar'
    const jobs = report.meta.jobs || [];

    const timelineData = useMemo(() => {
      if (!isMultiJob || !perJobData?.timeline) return report.applicationsOverTime;
      const allDates = new Set();
      Object.values(perJobData.timeline).forEach((dateMap) => Object.keys(dateMap).forEach((date) => allDates.add(date)));
      return Array.from(allDates).sort().map((date) => {
        const point = { date, label: formatDateLabel(date) };
        jobs.forEach((job) => { point[job.id] = perJobData.timeline[job.id]?.[date] || 0; });
        return point;
      });
    }, [isMultiJob, perJobData, report.applicationsOverTime, jobs]);

    return (
      <Panel
        title="Applications over time"
        eyebrow={isMultiJob ? `${jobs.length} jobs` : formatMonthLabel(report.meta.from) || 'selected period'}
        headerRight={
          <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/50">
            <button type="button" onClick={() => setChartType('area')} className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', chartType === 'area' ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700')}>Line</button>
            <button type="button" onClick={() => setChartType('bar')} className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', chartType === 'bar' ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700')}>Bar</button>
          </div>
        }
      >
        <div className="h-[240px] mt-2">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'area' ? (
              <AreaChart data={timelineData} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  {isMultiJob ? (
                    jobs.map((job, i) => (
                      <linearGradient key={job.id} id={`grad-${job.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={JOB_COLORS[i % JOB_COLORS.length]} stopOpacity={0.18} />
                        <stop offset="95%" stopColor={JOB_COLORS[i % JOB_COLORS.length]} stopOpacity={0.02} />
                      </linearGradient>
                    ))
                  ) : (
                    <linearGradient id="reportApplications" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1683f8" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#1683f8" stopOpacity={0.02} />
                    </linearGradient>
                  )}
                </defs>
                <CartesianGrid stroke="#edf2f7" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis hide />
                <Tooltip contentStyle={{ borderRadius: 10, borderColor: '#dbe4ef', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' }} />
                {isMultiJob ? (
                  jobs.map((job, i) => (
                    <Area key={job.id} type="monotone" dataKey={job.id} name={job.title} stroke={JOB_COLORS[i % JOB_COLORS.length]} strokeWidth={2} fill={`url(#grad-${job.id})`} fillOpacity={1} isAnimationActive={true} />
                  ))
                ) : (
                  <Area type="monotone" dataKey="count" stroke="#007BFF" strokeWidth={3} fill="url(#reportApplications)" isAnimationActive={true} />
                )}
              </AreaChart>
            ) : (
              <BarChart data={timelineData} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#edf2f7" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis hide />
                <Tooltip contentStyle={{ borderRadius: 10, borderColor: '#dbe4ef', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' }} cursor={{ fill: '#f1f5f9' }} />
                {isMultiJob ? (
                  jobs.map((job, i) => (
                    <Bar key={job.id} dataKey={job.id} name={job.title} fill={JOB_COLORS[i % JOB_COLORS.length]} radius={[4, 4, 0, 0]} isAnimationActive={true} />
                  ))
                ) : (
                  <Bar dataKey="count" fill="#007BFF" radius={[4, 4, 0, 0]} isAnimationActive={true} />
                )}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
        {isMultiJob && jobs.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-slate-100 pt-3">
            {jobs.map((job, i) => (
              <div key={job.id} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: JOB_COLORS[i % JOB_COLORS.length] }} />
                <span className="truncate max-w-[160px]">{job.title}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     ROSTER PANEL (raw data table)
     ────────────────────────────────────────────────────────────── */

  function RosterPanel({ report, columns, comparisonMode }) {
    const [selectedMonthTab, setSelectedMonthTab] = useState('all');
    const badgeClasses = { Selected: 'bg-green-50 text-green-700', Rejected: 'bg-red-50 text-red-700', Dropped: 'bg-orange-50 text-orange-700', Undecided: 'bg-blue-50 text-blue-600' };
    const showAssessment = !!report.meta.hasAssessment;
    const showInterview = !!report.meta.hasInterview;

    const isMonthMode = comparisonMode === 'month';
    const monthCols = columns || [];

    const monthCounts = useMemo(() => {
      if (!isMonthMode || !monthCols.length) return {};
      const map = {};
      monthCols.forEach((m) => {
        map[m.id] = 0;
      });
      (report.rawCandidates || []).forEach((c) => {
        const dateVal = c.appliedDate || c.appliedAt;
        if (!dateVal) return;
        const mId = String(dateVal).slice(0, 7);
        if (map[mId] !== undefined) {
          map[mId] += 1;
        }
      });
      return map;
    }, [isMonthMode, monthCols, report.rawCandidates]);

    const filteredCandidates = useMemo(() => {
      if (!isMonthMode || selectedMonthTab === 'all') {
        return report.rawCandidates || [];
      }
      return (report.rawCandidates || []).filter((c) => {
        const dateVal = c.appliedDate || c.appliedAt;
        if (!dateVal) return false;
        return String(dateVal).slice(0, 7) === selectedMonthTab;
      });
    }, [isMonthMode, selectedMonthTab, report.rawCandidates]);

    return (
      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Raw data - Candidate roster
          </h2>
          <span className="text-xs text-slate-400">{filteredCandidates.length} candidates</span>
        </div>

        {/* Month Filter Tabs (Only in By Month mode) */}
        {isMonthMode && monthCols.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xs">
            <button
              type="button"
              onClick={() => setSelectedMonthTab('all')}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-bold transition-all',
                selectedMonthTab === 'all'
                  ? 'bg-[#007BFF] text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
            >
              All ({report.summary.totalCandidates})
            </button>
            {monthCols.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedMonthTab(m.id)}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-xs font-bold transition-all flex items-center gap-1.5',
                  selectedMonthTab === m.id
                    ? 'bg-[#007BFF] text-white shadow-xs'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <span>{m.title}</span>
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-black',
                    selectedMonthTab === m.id
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-100 text-slate-700'
                  )}
                >
                  {monthCounts[m.id] || 0}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-2 sm:p-4 shadow-sm">
          <div className="max-h-[600px] overflow-auto minimal-scrollbar relative">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
                <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Candidate Name</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Hiring Position</th>
                  {showInterview && <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Interview Score</th>}
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">ARM Name</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">ARM Email</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Store Code</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Store Name</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Store City</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">State</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Region</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Category</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Hiring Decision</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Applied Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map((candidate) => (
                  <tr
                    key={`${candidate.candidate}-${candidate.appliedAt}`}
                    className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50/70"
                  >
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-xs font-black text-[#007BFF]">
                          {candidate.initials}
                        </span>
                        <span className="font-semibold text-slate-950">{candidate.candidate}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.jobTitle || '-'}</td>
                    {showInterview && (
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.interviewScore ?? '-'}</td>
                    )}
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.armName || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.armEmail || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.storeId || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.storeName || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.storeCity || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.storeState || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.storeRegion || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.storeCategory || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span
                        className={cn(
                          'rounded-full px-3 py-1 text-xs font-bold',
                          badgeClasses[candidate.outcome] || badgeClasses.Undecided
                        )}
                      >
                        {candidate.outcome}
                      </span>
                      {isOnboardedCandidate(candidate) && (
                        <span className="ml-1.5 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-700">
                          {ONBOARDED_LABEL}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.appliedAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     EXCEL EXPORT HELPERS
     ────────────────────────────────────────────────────────────── */

  function applySheetDefaults(
    ws,
    { freezeHeader = true, widths = [], autoFilter = true, headerRow = 0 } = {}
  ) {
    ws['!cols'] = widths.map((wch) => ({ wch }));
    if (freezeHeader) ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
    if (autoFilter) {
      const filterRange = { s: { r: headerRow, c: range.s.c }, e: { r: range.e.r, c: range.e.c } };
      ws['!autofilter'] = { ref: XLSX.utils.encode_range(filterRange) };
    }
    return ws;
  }

  function setNumberFormat(ws, addresses, format) {
    addresses.forEach((address) => { if (ws[address]) ws[address].z = format; });
  }

  function appendAoaSheet(wb, name, rows, widths, options = {}) {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    applySheetDefaults(ws, { widths, ...options });
    XLSX.utils.book_append_sheet(wb, ws, name);
    return ws;
  }

  function appendJsonSheet(wb, name, rows, widths, options = {}) {
    const ws = XLSX.utils.json_to_sheet(rows);
    applySheetDefaults(ws, { widths, ...options });
    XLSX.utils.book_append_sheet(wb, ws, name);
    return ws;
  }

  function buildGroupedReportRowsAoa(candidates, labelName, getGroupKey) {
    const groups = new Map();
    (candidates || []).forEach((c) => {
      const key = String(getGroupKey(c) || '').trim() || 'Unassigned';
      if (!groups.has(key)) {
        groups.set(key, { name: key, total: 0, selected: 0, rejected: 0, dropped: 0, undecided: 0 });
      }
      const g = groups.get(key);
      g.total += 1;
      if (c.outcome === 'Selected') g.selected += 1;
      if (c.outcome === 'Dropped') g.dropped += 1;
      else if (c.outcome === 'Rejected') g.rejected += 1;
      else g.undecided += 1;
    });

    const header = [labelName, 'Total Candidates', 'Selected', 'Rejected', 'Dropped', 'Undecided'];
    const rows = Array.from(groups.values())
      .sort((a, b) => b.total - a.total)
      .map((g) => [g.name, g.total, g.selected, g.rejected, g.dropped, g.undecided]);

    return [header, ...rows];
  }

  function generateAsciiBar(pct, maxLength = 10) {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    const filled = Math.round((p / 100) * maxLength);
    const empty = maxLength - filled;
    return '█'.repeat(filled) + '░'.repeat(empty) + ` ${p.toFixed(1)}%`;
  }

  /* ──────────────────────────────────────────────────────────────────
     EXPORT EXCEL
     ────────────────────────────────────────────────────────────── */

  function renderComparisonChartCanvas(title, categories, seriesData, width = 750, height = 400) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(title, width / 2, 34);

    const paddingLeft = 55;
    const paddingRight = 35;
    const paddingTop = 65;
    const paddingBottom = 75;
    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    let maxVal = 0;
    seriesData.forEach((s) => {
      (s.values || []).forEach((v) => {
        if (v > maxVal) maxVal = v;
      });
    });
    if (maxVal <= 0) maxVal = 10;
    const yMax = Math.ceil(maxVal * 1.15);

    const steps = 5;
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#f1f5f9';
    ctx.fillStyle = '#64748b';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';

    for (let i = 0; i <= steps; i++) {
      const val = Math.round((yMax / steps) * i);
      const y = paddingTop + chartHeight - (i / steps) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(paddingLeft, y);
      ctx.lineTo(paddingLeft + chartWidth, y);
      ctx.stroke();
      ctx.fillText(String(val), paddingLeft - 8, y + 4);
    }

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(paddingLeft, paddingTop + chartHeight);
    ctx.lineTo(paddingLeft + chartWidth, paddingTop + chartHeight);
    ctx.stroke();

    const numCats = categories.length;
    const catWidth = chartWidth / numCats;
    const numSeries = seriesData.length;
    const barGap = 6;
    const totalBarGroupWidth = Math.min(catWidth * 0.75, numSeries * 42);
    const barWidth = Math.max(14, (totalBarGroupWidth - (numSeries - 1) * barGap) / numSeries);

    const seriesColors = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#06b6d4'];

    categories.forEach((catLabel, catIdx) => {
      const groupCenterX = paddingLeft + catIdx * catWidth + catWidth / 2;
      const groupStartX = groupCenterX - ((numSeries * barWidth + (numSeries - 1) * barGap) / 2);

      seriesData.forEach((series, sIdx) => {
        const val = series.values[catIdx] || 0;
        const barH = (val / yMax) * chartHeight;
        const x = groupStartX + sIdx * (barWidth + barGap);
        const y = paddingTop + chartHeight - barH;

        ctx.fillStyle = series.color || seriesColors[sIdx % seriesColors.length];
        ctx.fillRect(x, y, barWidth, Math.max(barH, 2));

        ctx.fillStyle = '#0f172a';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(val), x + barWidth / 2, Math.max(paddingTop + 14, y - 6));
      });

      ctx.fillStyle = '#334155';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(catLabel, groupCenterX, paddingTop + chartHeight + 22);
    });

    const legendY = height - 20;
    let legendX = width / 2 - (seriesData.length * 110) / 2;

    seriesData.forEach((series, sIdx) => {
      const color = series.color || seriesColors[sIdx % seriesColors.length];
      ctx.fillStyle = color;
      ctx.fillRect(legendX, legendY - 10, 14, 14);

      ctx.fillStyle = '#334155';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(series.name, legendX + 20, legendY);

      legendX += 110;
    });

    return canvas.toDataURL('image/png');
  }

  async function embedImageInXlsx(xlsxArrayBuffer, pngDataUrl, startCol = 0, startRow = 16) {
    if (!pngDataUrl) return xlsxArrayBuffer;
    try {
      const zip = await JSZip.loadAsync(xlsxArrayBuffer);

      // Decode PNG base64 to bytes
      const base64Data = pngDataUrl.replace(/^data:image\/png;base64,/, '');
      const binaryStr = window.atob(base64Data);
      const imageBytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        imageBytes[i] = binaryStr.charCodeAt(i);
      }
      zip.file('xl/media/image1.png', imageBytes);

      // Properly formed drawing XML with correct namespaces and EMU-based offsets
      // EMU = English Metric Units; 914400 EMU = 1 inch; colOff/rowOff must be in EMU
      const drawingXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr
  xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:xdr14="http://schemas.microsoft.com/office/drawing/2010/main">
  <xdr:oneCellAnchor>
    <xdr:from>
      <xdr:col>${startCol}</xdr:col>
      <xdr:colOff>0</xdr:colOff>
      <xdr:row>${startRow}</xdr:row>
      <xdr:rowOff>0</xdr:rowOff>
    </xdr:from>
    <xdr:ext cx="6858000" cy="3810000"/>
    <xdr:pic>
      <xdr:nvPicPr>
        <xdr:cNvPr id="2" name="Chart_Comparison" descr="Monthly Comparison Chart"/>
        <xdr:cNvPicPr preferRelativeResize="0">
          <a:picLocks noChangeAspect="1"/>
        </xdr:cNvPicPr>
      </xdr:nvPicPr>
      <xdr:blipFill>
        <a:blip r:embed="rId1" cstate="print">
          <a:extLst>
            <a:ext uri="{28A0092B-C50C-407E-A947-70E740481C1C}">
              <a14:useLocalDpi xmlns:a14="http://schemas.microsoft.com/office/drawing/2010/main" val="0"/>
            </a:ext>
          </a:extLst>
        </a:blip>
        <a:stretch>
          <a:fillRect/>
        </a:stretch>
      </xdr:blipFill>
      <xdr:spPr>
        <a:xfrm>
          <a:off x="0" y="0"/>
          <a:ext cx="6858000" cy="3810000"/>
        </a:xfrm>
        <a:prstGeom prst="rect">
          <a:avLst/>
        </a:prstGeom>
      </xdr:spPr>
    </xdr:pic>
    <xdr:clientData/>
  </xdr:oneCellAnchor>
</xdr:wsDr>`;
      zip.file('xl/drawings/drawing1.xml', drawingXml);

      // Drawing relationships file
      const drawingRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image1.png"/>
</Relationships>`;
      zip.file('xl/drawings/_rels/drawing1.xml.rels', drawingRels);

      // Patch sheet1.xml to add drawing reference before </worksheet>
      // Must be after <pageSetup/> or similar; we add it just before </worksheet>
      let sheetXml = await zip.file('xl/worksheets/sheet1.xml').async('text');

      // Ensure the worksheet has the r namespace declared
      if (!sheetXml.includes('xmlns:r=')) {
        sheetXml = sheetXml.replace(
          '<worksheet ',
          '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" '
        );
      }
      // Add drawing element if not already present
      if (!sheetXml.includes('<drawing ')) {
        sheetXml = sheetXml.replace('</worksheet>', '<drawing r:id="rId2"/></worksheet>');
      }
      zip.file('xl/worksheets/sheet1.xml', sheetXml);

      // Patch or create _rels/sheet1.xml.rels with drawing relationship
      let sheetRels = '';
      const sheetRelsFile = zip.file('xl/worksheets/_rels/sheet1.xml.rels');
      if (sheetRelsFile) {
        sheetRels = await sheetRelsFile.async('text');
      } else {
        sheetRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`;
      }
      if (!sheetRels.includes('drawings/drawing1.xml')) {
        sheetRels = sheetRels.replace(
          '</Relationships>',
          '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/></Relationships>'
        );
        zip.file('xl/worksheets/_rels/sheet1.xml.rels', sheetRels);
      }

      // Patch [Content_Types].xml
      let contentTypes = await zip.file('[Content_Types].xml').async('text');
      if (!contentTypes.includes('image/png')) {
        contentTypes = contentTypes.replace(
          '</Types>',
          '<Default Extension="png" ContentType="image/png"/></Types>'
        );
      }
      if (!contentTypes.includes('drawings/drawing1.xml')) {
        contentTypes = contentTypes.replace(
          '</Types>',
          '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>'
        );
      }
      zip.file('[Content_Types].xml', contentTypes);

      return await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE', compressionOptions: { level: 6 } });
    } catch (err) {
      console.error('Failed to embed chart image into Excel:', err);
      return xlsxArrayBuffer;
    }
  }

  async function exportExcel(report, options = {}) {
    const { comparisonMode, monthColumns = [], perMonthData, perJobData, isSelectedView = false } = options;
    const isMonthMode = comparisonMode === 'month' && monthColumns.length > 0;
    // Location sheets follow the on-screen Total / Selected switch; outcome, funnel and roster
    // sheets always cover every applicant.
    const monthCategories = (isSelectedView ? options.categoryMonthData : perMonthData)?.categories;
    const jobCategories = (isSelectedView ? options.categoryJobData : perJobData)?.categories;
    const categoryTotal = isSelectedView ? options.categoryTotal || 0 : report.summary.totalCandidates;
    const categoryCountLabel = isSelectedView ? 'Total Selected' : 'Total Applicants';
    const showOnboarded = hasOnboarding(report);
    const funnelStages = funnelStagesFor(report);

    // `[label, ...perColumnCounts, total, share %, bar]` for each outcome across `ids` columns of
    // `data` (perJobData / perMonthData shape). Onboarded sits under Selected and is part of it,
    // so callers build their Total row from the four outcomes only.
    const outcomeRowsFor = (ids, data, denominator) => {
      const row = (label, counts) => {
        const total = counts.reduce((sum, n) => sum + n, 0);
        const pct = denominator ? (total / denominator) * 100 : 0;
        return [label, ...counts, total, `${pct.toFixed(1)}%`, generateAsciiBar(pct)];
      };
      return ['Selected', 'Rejected', 'Dropped', 'Undecided'].flatMap((key) => {
        const rows = [row(key, ids.map((id) => data?.outcomes?.[id]?.[key] || 0))];
        if (key === 'Selected' && showOnboarded) {
          rows.push(row(ONBOARDED_EXPORT_LABEL, ids.map((id) => data?.funnel?.[id]?.onboarded || 0)));
        }
        return rows;
      });
    };
    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: `TalentCrux Report - ${report.meta.jobTitle || 'Hiring Analytics'}`,
      Subject: 'Hiring analytics report',
      Author: 'TalentCrux',
      CreatedDate: new Date(),
    };

    const applyTableStyles = (ws, rows, colCount, skipRows = 0) => {
      const titleStyle = {
        font: { bold: true, color: { rgb: "1E3A8A" } },
        fill: { patternType: "solid", fgColor: { rgb: "DBEAFE" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };
      const dataStyle = {
        font: { color: { rgb: "000000" } }
      };
      const thinBorder = {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      };

      rows.forEach((row, rIdx) => {
        if (rIdx < skipRows) return;
        if (row.length === 0) return;

        const firstCell = row[0];
        const isTitle = typeof firstCell === 'string' && (
          firstCell.startsWith('Job:') || 
          firstCell.startsWith('Total Across') || 
          firstCell.startsWith('Summary Outcome') || 
          firstCell.startsWith('Summary Hiring')
        );
        
        const isHeader = 
          (row.includes('Outcome') && (row.includes('Count') || row.includes('Total'))) || 
          (row.includes('Stage') && (row.includes('Conversion Rate') || row.includes('Overall Rate'))) || 
          row.includes('Total Applicants') ||
          row.includes('Total Selected') ||
          row.includes('Report Mode') ||
          (row.includes('Month') && row.includes('Applied Candidates'));

        const colsToStyle = (row.length > 1 || isTitle) ? colCount : row.length;

        for (let c = 0; c < colsToStyle; c++) {
          let cell = ws[XLSX.utils.encode_cell({r: rIdx, c: c})];
          if (!cell) {
            cell = { t: 's', v: '' };
            ws[XLSX.utils.encode_cell({r: rIdx, c: c})] = cell;
          }
          cell.s = cell.s || {};
          cell.s.border = thinBorder;

          if (isTitle) {
            cell.s.fill = titleStyle.fill;
            cell.s.font = titleStyle.font;
            cell.s.alignment = titleStyle.alignment;
          } else if (isHeader) {
            cell.s.fill = headerStyle.fill;
            cell.s.font = headerStyle.font;
            cell.s.alignment = headerStyle.alignment;
          } else {
             cell.s.font = cell.s.font || dataStyle.font;
          }
        }
      });

      if (ws['!ref']) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        range.e.c = Math.max(range.e.c, colCount - 1);
        ws['!ref'] = XLSX.utils.encode_range(range);
      }
    };

    const { summaryRows: armSummaryRows, detailRows: armDetailRows } = buildArmReportRows(
      report.rawCandidates || []
    );

    const outcomeData = withOnboardedOutcome(
      report.outcomeBreakdown.filter(x => !['On hold', 'Dropped'].includes(x.name)),
      report
    );
    const uniqueRegions = new Set((report.rawCandidates || []).map(c => c.storeRegion).filter(Boolean));
    let regionName = 'All Regions';
    if (uniqueRegions.size > 1) regionName = 'Multiple Regions';
    else if (uniqueRegions.size === 1) regionName = Array.from(uniqueRegions)[0];

    const funnelData = withOnboardedFunnelStage(report.funnel.filter(x => x.stage !== 'Resume scored'), report);

    // 1. Overview Sheet
    const overviewRows = [
      ['TalentCrux Hiring Report'],
      ['Report Mode', isMonthMode ? 'Monthly Breakdown (By Month)' : 'Job Post Breakdown'],
      ['Job Posts Selected', report.meta.jobTitle || 'Selected Jobs'],
      ['Location Sheets', isSelectedView ? 'Selected candidates only' : 'All applicants'],
      ['Region', regionName],
      ['Date Frame', `${formatExportDate(report.meta.from)} to ${formatExportDate(report.meta.to)}`],
      ['Client Name', 'Go Colours'],
      [],
    ];

    let overviewColWidths = [];

    if (isMonthMode) {
      overviewRows.push(
        ['Summary Outcome Breakdown'],
        ['Outcome', 'Count', 'Percentage', 'Visual Bar'],
        ...outcomeData.map((x) => [x.name, x.value, x.pct / 100, generateAsciiBar(x.pct)]),
        [],
        ['Summary Hiring Funnel'],
        ['Stage', 'Count', 'Conversion Rate'],
        ...funnelData.map((x) => [
          x.stage,
          x.count,
          report.summary.totalCandidates ? `${((x.count / report.summary.totalCandidates) * 100).toFixed(1)}%` : '0%',
        ])
      );
      overviewColWidths = [26, 18, 16, 22];
    } else {
      const jTitles = (report.meta.jobs || []).map(j => j.title || j.id);
      const jIds = (report.meta.jobs || []).map(j => j.id);

      overviewRows.push(['Summary Outcome Breakdown']);
      overviewRows.push(['Outcome', ...jTitles, 'Total', 'Share %', 'Visual Bar']);

      overviewRows.push(...outcomeRowsFor(jIds, perJobData, report.summary.totalCandidates));
      const jobTotalsRow = jIds.map((jId) => {
        const o = perJobData?.outcomes?.[jId] || {};
        return (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
      });
      overviewRows.push(['Total', ...jobTotalsRow, report.summary.totalCandidates || 0, '100.0%', generateAsciiBar(100)]);

      overviewRows.push([]);
      overviewRows.push(['Summary Hiring Funnel']);
      overviewRows.push(['Stage', ...jTitles, 'Total', 'Overall Rate']);

      funnelStages.forEach((stg) => {
        const rowCounts = jIds.map((jId) => perJobData?.funnel?.[jId]?.[stg.key] || 0);
        const total = rowCounts.reduce((sum, n) => sum + n, 0);
        const rate = report.summary.totalCandidates ? `${((total / report.summary.totalCandidates) * 100).toFixed(1)}%` : '0%';
        overviewRows.push([stg.label, ...rowCounts, total, rate]);
      });
      
      overviewColWidths = [26, ...jTitles.map(() => 12), 12, 14, 22];
    }

    const overviewWs = appendAoaSheet(wb, 'Overview', overviewRows, overviewColWidths, { autoFilter: false, freezeHeader: false });
    overviewWs['!rows'] = [{ hpt: 22 }, {}, {}, {}, {}, {}, { hpt: 18 }];
    
    if (isMonthMode) {
      Object.keys(overviewWs).filter((key) => /^C\d+$/.test(key)).forEach((key) => {
        const rowNum = parseInt(key.substring(1), 10);
        if (rowNum >= 10 && rowNum <= 9 + outcomeData.length) {
          overviewWs[key].z = '0.0%';
        }
      });
    }
    
    // Apply styles to Overview, skipping the first 7 metadata rows
    applyTableStyles(overviewWs, overviewRows, overviewColWidths.length, 7);

    // Generate Chart Image
    let chartPngUrl = null;
    if (isMonthMode && perMonthData && monthColumns.length > 0) {
      const chartTitle = monthColumns.length >= 2
        ? `${monthColumns[0].title} to ${monthColumns[monthColumns.length - 1].title} Comparison`
        : `${monthColumns[0].title} Outcome Breakdown`;

      const chartCategories = ['Selected', 'Rejected', 'Dropped', 'Undecided', 'Total'];
      const chartSeries = monthColumns.slice(0, 5).map((m) => {
        const o = perMonthData.outcomes[m.id] || {};
        const sel = o.Selected || 0;
        const dropCount = o.Dropped || 0;
        const rej = o.Rejected || 0;
        const und = o.Undecided || 0;
        const tot = sel + rej + dropCount + und;
        return {
          name: m.title,
          values: [sel, rej, dropCount, und, tot],
        };
      });

      chartPngUrl = renderComparisonChartCanvas(chartTitle, chartCategories, chartSeries, 760, 380);
    }

    // 2. Month-Wise Breakdown Sheets (if comparisonMode === 'month')
    if (isMonthMode && perMonthData) {
      const mTitles = monthColumns.map((m) => m.title);
      const mIds = monthColumns.map((m) => m.id);

      const jobs = report.meta.jobs || [];

      // (A) Outcome by Month Sheet (Stacked per job)
      const outcomeMonthRows = [];
      outcomeMonthRows.push(['Total Across All Jobs', '', '', '']);
      outcomeMonthRows.push(['Outcome', ...mTitles, 'Total', 'Share %', 'Visual Bar']);
      
      outcomeMonthRows.push(...outcomeRowsFor(mIds, perMonthData, report.summary.totalCandidates));
      
      const monthTotalsRow = mIds.map((mId) => {
        const o = perMonthData.outcomes[mId] || {};
        return (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
      });
      outcomeMonthRows.push(['Total', ...monthTotalsRow, report.summary.totalCandidates || 0, '100.0%', generateAsciiBar(100)]);
      outcomeMonthRows.push([]); // separator

      jobs.forEach((job) => {
        const jobCandidates = (report.rawCandidates || []).filter(c => c.jobId === job.id);
        const jobMonthData = computePerMonthBreakdowns(jobCandidates, monthColumns) || { outcomes: {}, funnel: {} };
        const jobTotal = jobCandidates.length;

        outcomeMonthRows.push([`Job: ${job.title || job.id}`, '', '', '']);
        outcomeMonthRows.push(['Outcome', ...mTitles, 'Total', 'Share %', 'Visual Bar']);
        
        outcomeMonthRows.push(...outcomeRowsFor(mIds, jobMonthData, jobTotal));
        
        const jobMonthTotalsRow = mIds.map((mId) => {
          const o = jobMonthData.outcomes[mId] || {};
          return (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
        });
        outcomeMonthRows.push(['Total', ...jobMonthTotalsRow, jobTotal, '100.0%', generateAsciiBar(100)]);
        outcomeMonthRows.push([]); // separator
      });
      const outcomeMonthWs = appendAoaSheet(wb, 'Outcome by Month', outcomeMonthRows, [16, ...mTitles.map(() => 12), 12, 14, 22], { autoFilter: false });
      applyTableStyles(outcomeMonthWs, outcomeMonthRows, mTitles.length + 4);

      // (B) Hiring Funnel by Month Sheet (Stacked per job)
      const funnelMonthRows = [];
      funnelMonthRows.push(['Total Across All Jobs', '', '', '']);
      funnelMonthRows.push(['Stage', ...mTitles, 'Total', 'Overall Rate']);

      funnelStages.forEach((stg) => {
        const rowCounts = mIds.map((mId) => perMonthData.funnel[mId]?.[stg.key] || 0);
        const total = rowCounts.reduce((sum, n) => sum + n, 0);
        const rate = report.summary.totalCandidates ? `${((total / report.summary.totalCandidates) * 100).toFixed(1)}%` : '0%';
        funnelMonthRows.push([stg.label, ...rowCounts, total, rate]);
      });
      funnelMonthRows.push([]); // separator

      jobs.forEach((job) => {
        const jobCandidates = (report.rawCandidates || []).filter(c => c.jobId === job.id);
        const jobMonthData = computePerMonthBreakdowns(jobCandidates, monthColumns) || { outcomes: {}, funnel: {} };
        const jobTotal = jobCandidates.length;

        funnelMonthRows.push([`Job: ${job.title || job.id}`, '', '', '']);
        funnelMonthRows.push(['Stage', ...mTitles, 'Total', 'Overall Rate']);
        
        funnelStages.forEach((stg) => {
          const rowCounts = mIds.map((mId) => jobMonthData.funnel[mId]?.[stg.key] || 0);
          const total = rowCounts.reduce((sum, n) => sum + n, 0);
          const rate = jobTotal ? `${((total / jobTotal) * 100).toFixed(1)}%` : '0%';
          funnelMonthRows.push([stg.label, ...rowCounts, total, rate]);
        });
        funnelMonthRows.push([]); // separator
      });
      const funnelMonthWs = appendAoaSheet(wb, 'Hiring Funnel by Month', funnelMonthRows, [20, ...mTitles.map(() => 12), 12, 16], { autoFilter: false });
      applyTableStyles(funnelMonthWs, funnelMonthRows, mTitles.length + 3);

      // (C) Monthly State Breakdown Sheet
      if (monthCategories?.state?.length) {
        const stateMonthRows = [
          ['State', ...mTitles, categoryCountLabel, 'Share %', 'Visual Distribution'],
        ];
        monthCategories.state.forEach((st) => {
          const mCounts = mIds.map((mId) => st.byColumn?.[mId] || 0);
          const pct = categoryTotal ? (st.count / categoryTotal) * 100 : 0;
          stateMonthRows.push([st.name || 'Unassigned', ...mCounts, st.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const stateWs = appendAoaSheet(wb, 'State Breakdown (Monthly)', stateMonthRows, [22, ...mTitles.map(() => 12), 16, 14, 22], { autoFilter: false });
        applyTableStyles(stateWs, stateMonthRows, mTitles.length + 4);
      }

      // (D) Monthly City Breakdown Sheet
      if (monthCategories?.city?.length) {
        const cityMonthRows = [
          ['City', ...mTitles, categoryCountLabel, 'Share %', 'Visual Distribution'],
        ];
        monthCategories.city.forEach((ct) => {
          const mCounts = mIds.map((mId) => ct.byColumn?.[mId] || 0);
          const pct = categoryTotal ? (ct.count / categoryTotal) * 100 : 0;
          cityMonthRows.push([ct.name || 'Unassigned', ...mCounts, ct.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const cityWs = appendAoaSheet(wb, 'City Breakdown (Monthly)', cityMonthRows, [22, ...mTitles.map(() => 12), 16, 14, 22], { autoFilter: false });
        applyTableStyles(cityWs, cityMonthRows, mTitles.length + 4);
      }

      // Monthly Region Breakdown Sheet (New)
      if (monthCategories?.region?.length) {
        const regionMonthRows = [
          ['Region', ...mTitles, categoryCountLabel, 'Share %', 'Visual Distribution'],
        ];
        monthCategories.region.forEach((rg) => {
          const mCounts = mIds.map((mId) => rg.byColumn?.[mId] || 0);
          const pct = categoryTotal ? (rg.count / categoryTotal) * 100 : 0;
          regionMonthRows.push([rg.name || 'Unassigned', ...mCounts, rg.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const regionWs = appendAoaSheet(wb, 'Region Breakdown (Monthly)', regionMonthRows, [22, ...mTitles.map(() => 12), 16, 14, 22], { autoFilter: false });
        applyTableStyles(regionWs, regionMonthRows, mTitles.length + 4);
      }

      // (E) Monthly ARM Summary Sheet
      if (monthCategories?.arm?.length) {
        const armMonthRows = [
          ['ARM Name', ...mTitles, categoryCountLabel, 'Share %', 'Visual Bar'],
        ];
        monthCategories.arm.forEach((arm) => {
          const mCounts = mIds.map((mId) => arm.byColumn?.[mId] || 0);
          const pct = categoryTotal ? (arm.count / categoryTotal) * 100 : 0;
          armMonthRows.push([arm.name || 'Unassigned', ...mCounts, arm.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const armWs = appendAoaSheet(wb, 'ARM Performance (Monthly)', armMonthRows, [26, ...mTitles.map(() => 12), 16, 14, 22], { autoFilter: false });
        applyTableStyles(armWs, armMonthRows, mTitles.length + 4);
      }

      // (F) Monthly Store Breakdown Sheet
      if (monthCategories?.store?.length) {
        const storeMonthRows = [
          ['Store Details', ...mTitles, categoryCountLabel, 'Share %', 'Visual Distribution'],
        ];
        monthCategories.store.forEach((st) => {
          const mCounts = mIds.map((mId) => st.byColumn?.[mId] || 0);
          const pct = categoryTotal ? (st.count / categoryTotal) * 100 : 0;
          storeMonthRows.push([st.name || 'Unassigned', ...mCounts, st.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const storeWs = appendAoaSheet(wb, 'Store Breakdown (Monthly)', storeMonthRows, [30, ...mTitles.map(() => 12), 16, 14, 22], { autoFilter: false });
        applyTableStyles(storeWs, storeMonthRows, mTitles.length + 4);
      }

      // (G) Monthly Applications Trend Sheet (Stacked per job)
      const timelineRows = [];
      timelineRows.push(['Total Across All Jobs', '', '', '']);
      timelineRows.push(['Month', 'Applied Candidates', 'Interviews', 'Selected', 'Selection Rate %', 'Trend Bar Visual']);

      monthColumns.forEach((m) => {
        const app = perMonthData.funnel[m.id]?.applied || 0;
        const intv = perMonthData.funnel[m.id]?.interviewsDone || 0;
        const sel = perMonthData.outcomes[m.id]?.Selected || 0;
        const rate = app > 0 ? (sel / app) * 100 : 0;
        timelineRows.push([m.title, app, intv, sel, `${rate.toFixed(1)}%`, generateAsciiBar(rate)]);
      });
      timelineRows.push([]); // separator

      jobs.forEach((job) => {
        const jobCandidates = (report.rawCandidates || []).filter(c => c.jobId === job.id);
        const jobMonthData = computePerMonthBreakdowns(jobCandidates, monthColumns) || { outcomes: {}, funnel: {} };

        timelineRows.push([`Job: ${job.title || job.id}`, '', '', '']);
        timelineRows.push(['Month', 'Applied Candidates', 'Interviews', 'Selected', 'Selection Rate %', 'Trend Bar Visual']);
        
        monthColumns.forEach((m) => {
          const app = jobMonthData.funnel[m.id]?.applied || 0;
          const intv = jobMonthData.funnel[m.id]?.interviewsDone || 0;
          const sel = jobMonthData.outcomes[m.id]?.Selected || 0;
          const rate = app > 0 ? (sel / app) * 100 : 0;
          timelineRows.push([m.title, app, intv, sel, `${rate.toFixed(1)}%`, generateAsciiBar(rate)]);
        });
        timelineRows.push([]); // separator
      });
      const timelineWs = appendAoaSheet(wb, 'Monthly Applications Trend', timelineRows, [16, 20, 18, 14, 16, 24], { autoFilter: false });
      applyTableStyles(timelineWs, timelineRows, 6);
    } else {
      // Standard Job Post Breakdown Sheets
      const jobs = report.meta.jobs || [];
      const jTitles = jobs.map((j) => j.title || j.id);
      const jIds = jobs.map((j) => j.id);

      // (A) Per Job Outcome Summary Stacked Tables
      const outcomeStackedRows = [];
      outcomeStackedRows.push(['Total Across All Jobs', '', '', '']);
      outcomeStackedRows.push(['Outcome', 'Count', 'Percentage', 'Visual Bar']);
      const totalOutcomes = withOnboardedOutcome(
        report.outcomeBreakdown.filter(x => !['On hold', 'Dropped'].includes(x.name)),
        report
      );
      totalOutcomes.forEach(x => {
         outcomeStackedRows.push([x.name, x.value, `${(x.pct).toFixed(1)}%`, generateAsciiBar(x.pct)]);
      });
      outcomeStackedRows.push(['Total', report.summary.totalCandidates || 0, '100.0%', generateAsciiBar(100)]);
      outcomeStackedRows.push([]); // Empty row separator

      jobs.forEach((job) => {
        outcomeStackedRows.push([`Job: ${job.title || job.id}`, '', '', '']);
        outcomeStackedRows.push(['Outcome', 'Count', 'Percentage', 'Visual Bar']);
        
        const o = perJobData?.outcomes?.[job.id] || {};
        const sel = o.Selected || 0;
        const dropCount = o.Dropped || 0;
        const rej = o.Rejected || 0;
        const und = o.Undecided || 0;
        // Dropped is listed below, so it belongs in the total.
        const total = sel + rej + dropCount + und;
        
        const makeRow = (name, val) => {
           const pct = total ? (val / total) * 100 : 0;
           return [name, val, `${pct.toFixed(1)}%`, generateAsciiBar(pct)];
        };
        
        outcomeStackedRows.push(makeRow('Selected', sel));
        if (showOnboarded) {
          outcomeStackedRows.push(makeRow(ONBOARDED_EXPORT_LABEL, perJobData?.funnel?.[job.id]?.onboarded || 0));
        }
        outcomeStackedRows.push(makeRow('Dropped', dropCount));
        outcomeStackedRows.push(makeRow('Rejected', rej));
        outcomeStackedRows.push(makeRow('Undecided', und));
        outcomeStackedRows.push(['Total', total, '100.0%', generateAsciiBar(100)]);
        outcomeStackedRows.push([]); // Empty row separator
      });
      const outcomeWs = appendAoaSheet(wb, 'Outcome Summary', outcomeStackedRows, [22, 14, 16, 22], { autoFilter: false });
      applyTableStyles(outcomeWs, outcomeStackedRows, 4);

      // (B) Per Job Hiring Funnel Stacked Tables
      const funnelStackedRows = [];
      funnelStackedRows.push(['Total Across All Jobs', '', '']);
      funnelStackedRows.push(['Stage', 'Count', 'Conversion Rate']);
      const totalFunnel = withOnboardedFunnelStage(report.funnel.filter(x => x.stage !== 'Resume scored'), report);
      totalFunnel.forEach(x => {
         const rate = report.summary.totalCandidates ? (x.count / report.summary.totalCandidates) * 100 : 0;
         funnelStackedRows.push([x.stage, x.count, `${rate.toFixed(1)}%`]);
      });
      funnelStackedRows.push([]); // Empty row separator

      jobs.forEach((job) => {
        funnelStackedRows.push([`Job: ${job.title || job.id}`, '', '']);
        funnelStackedRows.push(['Stage', 'Count', 'Conversion Rate']);
        
        const f = perJobData?.funnel?.[job.id] || {};
        const app = f.applied || 0;
        const intv = f.interviewsDone || 0;
        const sel = f.selected || 0;
        
        const makeRow = (name, val) => {
           const rate = app ? (val / app) * 100 : 0;
           return [name, val, `${rate.toFixed(1)}%`];
        };
        
        funnelStackedRows.push(makeRow('Applied', app));
        funnelStackedRows.push(makeRow('Interviews', intv));
        funnelStackedRows.push(makeRow('Selected', sel));
        if (showOnboarded) funnelStackedRows.push(makeRow(ONBOARDED_LABEL, f.onboarded || 0));
        funnelStackedRows.push([]);
      });
      const funnelWs = appendAoaSheet(wb, 'Hiring Funnel', funnelStackedRows, [24, 14, 16], { autoFilter: false });
      applyTableStyles(funnelWs, funnelStackedRows, 3);

      // (C) Category Sheets with Job Columns
      const buildCategorySheet = (sheetName, categoryKey, titleColName) => {
        const catData = jobCategories?.[categoryKey];
        if (catData?.length) {
          const rows = [[titleColName, ...jTitles, categoryCountLabel, 'Share %', 'Visual Distribution']];
          catData.forEach((st) => {
            const mCounts = jIds.map((jId) => st.byColumn?.[jId] || 0);
            const pct = categoryTotal ? (st.count / categoryTotal) * 100 : 0;
            rows.push([st.name || 'Unassigned', ...mCounts, st.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
          });
          const ws = appendAoaSheet(wb, sheetName, rows, [22, ...jTitles.map(() => 12), 16, 14, 22]);
          applyTableStyles(ws, rows, jTitles.length + 4);
        }
      };

      buildCategorySheet('Candidates By States', 'state', 'State');
      buildCategorySheet('Candidates By City', 'city', 'City');
      buildCategorySheet('ARM Summary', 'arm', 'ARM Name');
      buildCategorySheet('Candidates By Store Code', 'store', 'Store Details');
      buildCategorySheet('Candidates By Region', 'region', 'Region');

      // (D) Applications Over Time
      const appTimeWs = appendJsonSheet(
        wb,
        'Applications Over Time',
        report.applicationsOverTime.map((x) => ({
          Date: x.date || x.label,
          Label: x.label,
          Applications: x.count,
          'Visual Bar': generateAsciiBar(report.summary.totalCandidates ? (x.count / report.summary.totalCandidates) * 100 : 0),
        })),
        [16, 16, 16, 22]
      );
      if (appTimeWs['!ref']) {
        const range = XLSX.utils.decode_range(appTimeWs['!ref']);
        const thinBorder = {
          top: { style: 'thin', color: { rgb: "000000" } },
          bottom: { style: 'thin', color: { rgb: "000000" } },
          left: { style: 'thin', color: { rgb: "000000" } },
          right: { style: 'thin', color: { rgb: "000000" } }
        };
        const headerStyle = {
          font: { bold: true, color: { rgb: "000000" } },
          fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
        for (let r = range.s.r; r <= range.e.r; r++) {
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cellAddr = XLSX.utils.encode_cell({r, c});
            let cell = appTimeWs[cellAddr];
            if (!cell) {
               cell = { t: 's', v: '' };
               appTimeWs[cellAddr] = cell;
            }
            cell.s = cell.s || {};
            cell.s.border = thinBorder;
            if (r === 0) {
              cell.s.fill = headerStyle.fill;
              cell.s.font = headerStyle.font;
              cell.s.alignment = headerStyle.alignment;
            }
          }
        }
      }
    }

    // ARM Candidate Details removed from By Month mode as requested

    const rawRows = (report.rawCandidates || []).map((c) => ({
      'Candidate Name': c.candidate,
      'Hiring Position': c.jobTitle || '',
      'Interview Score': c.interviewScore ?? '',
      'ARM Name': c.armName || '',
      'ARM Email': c.armEmail || '',
      'Store Code': c.storeId || '',
      'Store Name': c.storeName || '',
      'Store City': c.storeCity || '',
      State: c.storeState || '',
      Region: c.storeRegion || '',
      Category: c.storeCategory || '',
      'City Code': c.cityCode || '',
      'City Name': c.cityCity || '',
      'City State': c.cityState || '',
      'City Region': c.cityRegion || '',
      'Hiring Decision': c.outcome,
      ...(showOnboarded ? { Onboarded: isOnboardedCandidate(c) ? 'Yes' : 'No' } : {}),
      'Applied Date': c.appliedDate || c.appliedAt,
    }));
    const rawWidths = [22, 24, 16, 20, 28, 14, 24, 16, 16, 14, 18, 14, 16, 16, 16, 14, ...(showOnboarded ? [12] : []), 14];
    const rawWs = appendJsonSheet(wb, 'Raw Data - Roster', rawRows, rawWidths);
    if (rawWs['!ref']) {
      const range = XLSX.utils.decode_range(rawWs['!ref']);
      const thinBorder = {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cellAddr = XLSX.utils.encode_cell({r, c});
          let cell = rawWs[cellAddr];
          if (!cell) {
             cell = { t: 's', v: '' };
             rawWs[cellAddr] = cell;
          }
          cell.s = cell.s || {};
          cell.s.border = thinBorder;
          if (r === 0) {
            cell.s.fill = headerStyle.fill;
            cell.s.font = headerStyle.font;
            cell.s.alignment = headerStyle.alignment;
          }
        }
      }
    }

    const rawBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    let finalBuffer = rawBuffer;

    if (chartPngUrl) {
      // Place chart to the right of the Overview table with 1 column padding
      const chartStartCol = overviewColWidths.length + 1;
      const chartStartRow = 8;
      finalBuffer = await embedImageInXlsx(rawBuffer, chartPngUrl, chartStartCol, chartStartRow);
    }

    const blob = new Blob([finalBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filePrefix = isMonthMode
      ? `TalentCrux_Monthly_Report_${compactDate(report.meta.from)}-to-${compactDate(report.meta.to)}`
      : getReportFileBaseName(report);

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filePrefix}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  /* ──────────────────────────────────────────────────────────────────
     PDF EXPORT HELPERS
     ────────────────────────────────────────────────────────────── */

  function pdfColor(hex) {
    const clean = String(hex || '#0f172a').replace('#', '');
    const value =
      clean.length === 3
        ? clean
          .split('')
          .map((x) => x + x)
          .join('')
        : clean;
    return [
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16),
    ];
  }

  function drawPdfKpi(doc, x, y, w, h, title, value, detail, color = '#007BFF') {
    const [r, g, b] = pdfColor(color);
    doc.setDrawColor(219, 228, 239);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, w, h, 8, 8, 'FD');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'bold');
    doc.text(String(title).toUpperCase(), x + 12, y + 18);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(20);
    doc.text(String(value), x + 12, y + 42);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(String(detail || ''), x + 12, y + 59, { maxWidth: w - 24 });
    doc.setFillColor(236, 242, 248);
    doc.roundedRect(x + 12, y + h - 13, w - 24, 2, 1, 1, 'F');
    doc.setFillColor(r, g, b);
    doc.roundedRect(x + 12, y + h - 13, (w - 24) * 0.72, 2, 1, 1, 'F');
  }

  function drawPdfBarList(doc, title, rows, x, y, w, options = {}) {
    const visibleRows = rows.slice(0, options.maxRows || rows.length);
    const hiddenRows = rows.slice(visibleRows.length);
    const displayRows = hiddenRows.length > 0 ? [...visibleRows, { name: `Others (${hiddenRows.length})`, count: hiddenRows.reduce((s, r) => s + (r.count ?? r.value ?? 0), 0), muted: true }] : visibleRows;
    const max = Math.max(...displayRows.map((r) => r.count || r.value || 0), 1);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(title, x, y);
    let cy = y + 22;
    displayRows.forEach((row) => {
      const label = row.name || row.band || row.stage || row.label;
      const count = row.count ?? row.value;
      const barW = Math.max(8, ((count || 0) / max) * (w - 118));
      doc.setFontSize(8.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(51, 65, 85);
      doc.text(String(label), x, cy + 7, { maxWidth: 85 });
      doc.setFillColor(236, 242, 248);
      doc.roundedRect(x + 96, cy, w - 118, 8, 4, 4, 'F');
      doc.setFillColor(...pdfColor(row.muted ? '#94a3b8' : options.color || '#2f9bff'));
      doc.roundedRect(x + 96, cy, barW, 8, 4, 4, 'F');
      doc.setTextColor(15, 23, 42);
      doc.setFont(undefined, 'bold');
      doc.text(String(count), x + w - 8, cy + 7, { align: 'right' });
      cy += options.rowGap || 18;
    });
  }

  /* ──────────────────────────────────────────────────────────────────
     EXPORT PDF
     ────────────────────────────────────────────────────────────── */

  function exportPdf(report) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFillColor(247, 249, 252);
    doc.rect(0, 0, 842, 595, 'F');
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(28, 24, 786, 78, 10, 10, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Reports', 44, 54);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Hiring analytics for a selected job and time frame - live, always current.', 44, 72);
    doc.text(`${report.meta.jobTitle} | ${report.meta.screeningName}`, 44, 88);
    doc.setTextColor(0, 123, 255);
    doc.setFont(undefined, 'bold');
    doc.text(`${formatExportDate(report.meta.from)} - ${formatExportDate(report.meta.to)}`, 790, 54, { align: 'right' });

    const kpiW = 146; const gap = 10;
    drawPdfKpi(doc, 32, 122, kpiW, 82, 'Total Candidates', report.summary.totalCandidates, `vs ${report.comparison.previousTotalCandidates} previous`);
    drawPdfKpi(doc, 32 + (kpiW + gap), 122, kpiW, 82, 'Selected', report.summary.selected, `vs ${report.comparison.previousSelected} previous`, '#16a34a');
    drawPdfKpi(doc, 32 + (kpiW + gap) * 2, 122, kpiW, 82, 'Selection Rate', `${report.summary.selectionRate}%`, `vs ${report.comparison.previousSelectionRate}%`);
    drawPdfKpi(doc, 32 + (kpiW + gap) * 3, 122, kpiW, 82, 'Avg Resume Score', report.summary.avgResumeScore, `vs ${report.comparison.previousAvgResumeScore}`);
    drawPdfKpi(doc, 32 + (kpiW + gap) * 4, 122, kpiW, 82, 'Rejected', report.summary.rejected, `vs ${report.comparison.previousRejected || 0} previous`, '#dc2626');

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(32, 224, 372, 142, 10, 10, 'F');
    doc.roundedRect(424, 224, 386, 142, 10, 10, 'F');
    drawPdfBarList(doc, 'Outcome breakdown', report.outcomeBreakdown, 52, 248, 322, { color: '#16a34a', maxRows: 4 });
    drawPdfBarList(doc, 'Resume score distribution', report.scoreDistribution, 444, 248, 336, { color: '#1683f8' });

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(32, 386, 372, 140, 10, 10, 'F');
    doc.roundedRect(424, 386, 386, 140, 10, 10, 'F');
    drawPdfBarList(doc, 'Hiring funnel', report.funnel, 52, 410, 322, { color: '#007BFF', rowGap: 17 });
    drawPdfBarList(doc, 'Candidates by location', report.locationBreakdown, 444, 410, 336, { color: '#2f9bff', maxRows: 4, rowGap: 17 });

    doc.addPage('a4', 'landscape');
    doc.setFillColor(247, 249, 252);
    doc.rect(0, 0, 842, 595, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Raw Data - Candidate Roster', 40, 44);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Showing ${report.rawCandidates.length} candidate rows exported from this report.`, 40, 62);

    autoTable(doc, {
      startY: 86,
      head: [
        [
          'Candidate',
          'Location',
          'Resume',
          'Assessment',
          'Interview',
          'Store City',
          'Expected CTC',
          'Applied',
          'Outcome',
        ],
      ],
      body: report.rawCandidates.map((c) => [
        c.candidate,
        c.location,
        c.resumeScore ?? '-',
        c.assessmentScore ?? '-',
        c.interviewScore ?? '-',
        c.storeCity || '-',
        c.expectedCtc,
        c.appliedAt,
        c.outcome,
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 5,
        textColor: [51, 65, 85],
        lineColor: [226, 232, 240],
        lineWidth: 0.5,
      },
      headStyles: { fillColor: [0, 123, 255] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('TalentCrux Reports', 40, 570);
      doc.text(`Page ${i} of ${pageCount}`, 802, 570, { align: 'right' });
    }

    doc.save(`${getReportFileBaseName(report)}.pdf`);
  }

  /* ──────────────────────────────────────────────────────────────────
     MAIN PAGE COMPONENT
     ────────────────────────────────────────────────────────────── */

  const customScrollbarCss = `
  .minimal-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .minimal-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .minimal-scrollbar::-webkit-scrollbar-thumb {
    background: #e2e8f0; 
    border-radius: 6px;
  }
  .minimal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1; 
  }
  /* Label column stays put while a comparison table scrolls sideways on a narrow screen.
     Sticky cells need an opaque background: tinted rows get the solid equivalent of their tint. */
  @media (max-width: 1023px) {
    .report-sticky-first th:first-child,
    .report-sticky-first td:first-child {
      position: sticky;
      left: 0;
      z-index: 1;
      background-color: #ffffff;
      box-shadow: 1px 0 0 0 #e2e8f0;
    }
    .report-sticky-first tr[class~='bg-green-50/40'] td:first-child { background-color: #f9fefb; }
    .report-sticky-first tr[class~='bg-teal-50/40'] td:first-child { background-color: #f9fefd; }
    .report-sticky-first tr[class~='bg-slate-50/60'] td:first-child { background-color: #fbfcfd; }
  }
`;


export default function ReportsPage() {
    const [timeFrame, setTimeFrame] = useState('1m');
    const [range, setRange] = useState(() => getDefaultRange('1m'));
    const [comparisonMode, setComparisonMode] = useState('job'); // 'job' | 'month'
    const [candidateView, setCandidateView] = useState('total'); // 'total' | 'selected' — location tables only
    const [activeModal, setActiveModal] = useState(null);
    const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
    const [tempCustomRange, setTempCustomRange] = useState({ from: '', to: '' });
    const [selectedJobIds, setSelectedJobIds] = useState([]);
    const [selectedScreeningIds, setSelectedScreeningIds] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const filterBarRef = useRef(null);
    const actionsMenuRef = useRef(null);
    const queryClient = useQueryClient();

    const storesQuery = useQuery({
      queryKey: ['stores'],
      queryFn: async () => {
        const response = await axios.get('/api/stores');
        return response.data?.data || [];
      },
      staleTime: 5 * 60 * 1000,
    });
    const availableStores = storesQuery.data || [];

    // Import Modal State
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importMode, setImportMode] = useState('new'); // 'new' | 'existing'
    const [selectedExistingJobId, setSelectedExistingJobId] = useState('');
    const [newJobTitle, setNewJobTitle] = useState('');
    const [importFile, setImportFile] = useState(null);
    const [parsedCandidates, setParsedCandidates] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedJobToDelete, setSelectedJobToDelete] = useState('');
    const [isDeletingJob, setIsDeletingJob] = useState(false);
    const [deleteJobError, setDeleteJobError] = useState(null);
    const [deleteJobSuccess, setDeleteJobSuccess] = useState(null);

    const validateCandidatesData = (candidatesList, storeList) => {
      const errs = [];
      const seenEmails = new Map();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const validOutcomes = ['selected', 'rejected', 'undecided', 'hired', 'pending', ''];

      const registeredStoreCodes = new Set(
        (storeList || []).map((s) => String(s.storeId || '').trim().toLowerCase()).filter(Boolean)
      );

      candidatesList.forEach((item, index) => {
        const rowNum = index + 2; // Header is Row 1
        const name = String(item.candidate || item.name || '').trim();
        const email = String(item.email || '').trim().toLowerCase();
        const rawOutcome = String(item.outcome || '').trim().toLowerCase();
        const rawStoreId = String(item.storeId || '').trim();

        if (!name) {
          errs.push({ row: rowNum, column: 'Candidate Name', message: 'Candidate Name is required.' });
        }

        if (!email) {
          errs.push({ row: rowNum, column: 'Email', message: 'Email address is required.' });
        } else if (!emailRegex.test(email)) {
          errs.push({ row: rowNum, column: 'Email', message: `Invalid email format ("${item.email}").` });
        } else if (seenEmails.has(email)) {
          errs.push({
            row: rowNum,
            column: 'Email',
            message: `Duplicate email "${email}" (already present in Row ${seenEmails.get(email)}).`,
          });
        } else {
          seenEmails.set(email, rowNum);
        }

        if (rawOutcome && !validOutcomes.some((o) => rawOutcome.includes(o))) {
          errs.push({
            row: rowNum,
            column: 'Outcome',
            message: `Invalid Outcome "${item.outcome}". Must be Selected, Rejected, or Undecided.`,
          });
        }

        if (rawStoreId && registeredStoreCodes.size > 0 && !registeredStoreCodes.has(rawStoreId.toLowerCase())) {
          errs.push({
            row: rowNum,
            column: 'Store ID',
            message: `Store ID "${rawStoreId}" does not exist in your registered Store database.`,
          });
        }

        if (item.resumeScore !== undefined && item.resumeScore !== null && item.resumeScore !== '') {
          const rs = Number(item.resumeScore);
          if (Number.isNaN(rs) || rs < 0 || rs > 100) {
            errs.push({
              row: rowNum,
              column: 'Resume Score',
              message: `Resume score must be a number between 0 and 100 (found "${item.resumeScore}").`,
            });
          }
        }

        if (item.assessmentScore !== undefined && item.assessmentScore !== null && item.assessmentScore !== '') {
          const as = Number(item.assessmentScore);
          if (Number.isNaN(as) || as < 0 || as > 100) {
            errs.push({
              row: rowNum,
              column: 'Assessment Score',
              message: `Assessment score must be a number between 0 and 100 (found "${item.assessmentScore}").`,
            });
          }
        }

        if (item.interviewScore !== undefined && item.interviewScore !== null && item.interviewScore !== '') {
          const is = Number(item.interviewScore);
          if (Number.isNaN(is) || is < 0 || is > 100) {
            errs.push({
              row: rowNum,
              column: 'Interview Score',
              message: `Interview score must be a number between 0 and 100 (found "${item.interviewScore}").`,
            });
          }
        }
      });

      return errs;
    };

    const handleDownloadSampleXls = () => {
      const sampleHeaders = [
        'Candidate Name',
        'Email',
        'Phone (Optional)',
        'Applied Date',
        'Outcome',
        'Interview Score (Optional)',
        'Store ID',
      ];

      const store1 = availableStores[0];
      const store2 = availableStores[1];
      const store3 = availableStores[2];

      const sampleRows = [
        [
          'Yasin',
          'yasin@example.com',
          '9876543210',
          '01-07-2026',
          'Selected',
          82,
          '1363',
        ],
        [
          'Priya Patel',
          'priya.p@example.com',
          '9876543211',
          '05-07-2026',
          'Undecided',
          70,
          store2?.storeId || '1120',
        ],
        [
          'Amit Kumar',
          'amit.k@example.com',
          '9876543212',
          '10-07-2026',
          'Rejected',
          40,
          store3?.storeId || '1131',
        ],
      ];

      const wsMain = XLSX.utils.aoa_to_sheet([sampleHeaders, ...sampleRows]);
      wsMain['!cols'] = sampleHeaders.map(() => ({ wch: 24 }));

      // Data validation dropdowns for Outcome (Column E) and Store ID (Column G)
      wsMain['!dataValidation'] = [
        {
          sqref: 'E2:E500',
          type: 'list',
          allowBlank: true,
          formula1: '"Selected,Rejected,Undecided"',
        },
        {
          sqref: 'G2:G500',
          type: 'list',
          allowBlank: true,
          formula1: `'Registered Stores'!$A$2:$A$${availableStores.length > 0 ? availableStores.length + 1 : 100}`,
        },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsMain, 'Candidate Import Format');

      // Create Sheet 2: Registered Stores Reference Table
      const storeHeaders = ['Store ID', 'Store Name', 'City', 'State', 'ARM Name', 'ARM Email'];
      const storeRows = availableStores.map((s) => [
        s.storeId || '',
        s.name || '',
        s.city || '',
        s.state || '',
        s.armName || '',
        s.armEmail || '',
      ]);

      const wsStores = XLSX.utils.aoa_to_sheet([storeHeaders, ...storeRows]);
      wsStores['!cols'] = storeHeaders.map(() => ({ wch: 22 }));
      XLSX.utils.book_append_sheet(wb, wsStores, 'Registered Stores');

      XLSX.writeFile(wb, 'TalentCrux_Sample_Candidate_Import_Format.xlsx');
    };

    const handleFileSelect = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setImportFile(file);
      setImportError(null);
      setValidationErrors([]);

      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          if (!rows.length) {
            setImportError('No candidate rows found in the selected file.');
            setParsedCandidates([]);
            setValidationErrors([]);
            return;
          }

          const mapped = rows
            .map((row) => {
              const getVal = (...keys) => {
                for (const k of keys) {
                  const foundKey = Object.keys(row).find((rk) =>
                    rk.trim().toLowerCase().includes(k.toLowerCase())
                  );
                  if (foundKey && row[foundKey] !== undefined && row[foundKey] !== '') {
                    return row[foundKey];
                  }
                }
                return '';
              };

              const city = getVal('city', 'store city');
              const state = getVal('state', 'store state');
              const locVal = getVal('location');
              const location = locVal || [city, state].filter(Boolean).join(', ');

              return {
                candidate: getVal('candidate name', 'name', 'full name', 'candidate'),
                email: getVal('email id', 'email', 'e-mail'),
                phone: getVal('phone', 'mobile', 'contact'),
                city,
                state,
                location,
                appliedAt: getVal('applied date', 'applied at', 'date'),
                outcome: getVal('outcome', 'status', 'selection'),
                resumeScore: getVal('resume score', 'resume'),
                assessmentScore: getVal('assessment score', 'assessment'),
                interviewScore: getVal('interview score', 'interview'),
                expectedCtc: getVal('expected ctc', 'ctc', 'salary'),
                armName: getVal('arm name', 'arm'),
                armEmail: getVal('arm email'),
                storeId: getVal('store id', 'store'),
              };
            })
            .filter((c) => c.candidate || c.email);

          if (!mapped.length) {
            setImportError('Could not find Candidate Name or Email columns in the file.');
            setParsedCandidates([]);
            setValidationErrors([]);
            return;
          }

          const errs = validateCandidatesData(mapped, availableStores);
          setValidationErrors(errs);
          setParsedCandidates(mapped);
        } catch (err) {
          console.error('Failed to parse Excel file:', err);
          setImportError('Failed to parse file. Please check file format and try again.');
          setParsedCandidates([]);
          setValidationErrors([]);
        }
      };
      reader.readAsArrayBuffer(file);
    };

    const handleExecuteImport = async () => {
      if (!parsedCandidates.length) {
        setImportError('Please select a valid Excel/CSV file containing candidates.');
        return;
      }

      const errs = validateCandidatesData(parsedCandidates, availableStores);
      if (errs.length > 0) {
        setValidationErrors(errs);
        setImportError(`Validation failed: Please fix all ${errs.length} row/column error(s) before importing.`);
        return;
      }

      setIsImporting(true);
      setImportError(null);
      setValidationErrors([]);

      try {
        let targetJobId = selectedExistingJobId;
        let targetScreeningId = null;

        if (importMode === 'new') {
          if (!newJobTitle.trim()) {
            setImportError('Please enter a Job Post Name for the external upload.');
            setIsImporting(false);
            return;
          }
          const createRes = await axios.post('/api/reports/create-external-job', {
            title: newJobTitle.trim(),
          });
          targetJobId = createRes.data.job._id;
          targetScreeningId = createRes.data.screening._id;
        } else {
          if (!targetJobId) {
            setImportError('Please select an existing Job Post.');
            setIsImporting(false);
            return;
          }
          const summaryRes = await axios.get(`/api/screenings/job/${targetJobId}/summary`);
          const screeningsList = summaryRes.data?.screenings || [];
          if (screeningsList.length > 0) {
            targetScreeningId = screeningsList[0]._id || screeningsList[0].id;
          } else {
            const createJobRes = await axios.post('/api/reports/create-external-job', {
              title: `Job ${targetJobId}`,
            });
            targetScreeningId = createJobRes.data.screening._id;
          }
        }

        await axios.post('/api/reports/import-candidates', {
          screeningId: targetScreeningId,
          candidates: parsedCandidates,
        });

        queryClient.invalidateQueries(['jobs']);
        setSelectedJobIds([targetJobId]);
        reportQuery.refetch();

        setImportSuccess(`Successfully imported ${parsedCandidates.length} candidates!`);
        setTimeout(() => {
          setIsImportModalOpen(false);
          setImportSuccess(null);
          setImportFile(null);
          setParsedCandidates([]);
          setValidationErrors([]);
          setNewJobTitle('');
        }, 1200);
      } catch (err) {
        console.error('Error importing candidates:', err);
        if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          setValidationErrors(err.response.data.errors);
        }
        setImportError(
          err.response?.data?.message || 'Failed to import candidates. Please fix file errors and try again.'
        );
      } finally {
        setIsImporting(false);
      }
    };

    const handleDeleteExternalJob = async () => {
      if (!selectedJobToDelete) {
        setDeleteJobError('Please select an external job post to delete.');
        return;
      }
      try {
        setIsDeletingJob(true);
        setDeleteJobError(null);
        setDeleteJobSuccess(null);

        await reportsApi.deleteExternalJobPost(selectedJobToDelete);

        setDeleteJobSuccess('External job post and all associated data deleted successfully!');

        queryClient.resetQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        queryClient.refetchQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['reports'] });

        setSelectedJobIds((prev) => prev.filter((id) => id !== selectedJobToDelete));

        setTimeout(() => {
          setIsDeleteModalOpen(false);
          setSelectedJobToDelete('');
          setDeleteJobSuccess(null);
        }, 1000);
      } catch (err) {
        console.error('Failed to delete external job post:', err);
        setDeleteJobError(
          err.response?.data?.message || 'Failed to delete external job post. Please try again.'
        );
      } finally {
        setIsDeletingJob(false);
      }
    };

    useEffect(() => {
      function handleClickOutside(e) {
        if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
          setIsActionsOpen(false);
        }
      }
      if (isActionsOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isActionsOpen]);

    const closeDropdowns = useCallback(() => { setJobDropdownOpen(false); }, []);

    useEffect(() => {
      function handleClick(e) { if (filterBarRef.current && !filterBarRef.current.contains(e.target)) closeDropdowns(); }
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, [closeDropdowns]);

    const { data: jobs = [], isLoading: jobsLoading } = useJobs(true, true);

    const screeningsQueries = useQuery({
      queryKey: ['reports', 'screenings', selectedJobIds],
      queryFn: async () => {
        const results = await Promise.all(selectedJobIds.map((jid) => axios.get(`/api/screenings/job/${jid}/summary`).then((r) => r.data?.screenings || [])));
        return results.flat();
      },
      enabled: selectedJobIds.length > 0,
      staleTime: 2 * 60 * 1000,
    });

    const screenings = screeningsQueries.data || [];

    useEffect(() => {
      if (screenings.length > 0) {
        const allScreeningIds = screenings.map((s) => String(s._id || s.id));
        setSelectedScreeningIds(allScreeningIds);
      } else {
        setSelectedScreeningIds([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenings.length, screenings.map((s) => String(s._id || s.id)).join(',')]);

    useEffect(() => {
      if (timeFrame !== 'custom') setRange(getDefaultRange(timeFrame));
    }, [timeFrame]);

    const reportParams = useMemo(() => {
      if (!selectedJobIds.length || !selectedScreeningIds.length || !range.from || !range.to) return null;
      const params = { jobId: selectedJobIds, screeningId: selectedScreeningIds, from: range.from, to: range.to };
      if (selectedRegion !== 'all') params.region = selectedRegion;
      return params;
    }, [range.from, range.to, selectedJobIds, selectedScreeningIds, selectedRegion]);

    const reportQuery = useQuery({
      queryKey: ['reports', 'screening-report', reportParams],
      queryFn: async () => reportsApi.getScreeningReport(reportParams),
      enabled: !!reportParams,
      retry: false,
    });

    const report = useMemo(() => {
      if (reportMatchesParams(reportQuery.data, reportParams)) return normalizeReportPayload(reportQuery.data, { from: range.from, to: range.to });
      return null;
    }, [range.from, range.to, reportParams, reportQuery.data]);

    const hasLiveData = !!report && reportMatchesParams(reportQuery.data, reportParams);
    const isLoadingReport = reportQuery.isFetching;
    const canLoadLiveReport = !!reportParams;
    // Surface request failures explicitly — without this a failed report renders
    // the same "select a job" empty state as having selected nothing at all.
    const reportErrorMessage = reportQuery.isError
      ? reportQuery.error?.response?.data?.message ||
        reportQuery.error?.message ||
        'The report request failed.'
      : null;
    const isMultiJob = (report?.meta?.jobs?.length || 0) > 1;

    const monthColumns = useMemo(() => {
      return getMonthsInRange(range.from, range.to);
    }, [range.from, range.to]);


    const perJobData = useMemo(() => {
      if (!report?.rawCandidates?.length || !report?.meta?.jobs?.length) return null;
      return computePerJobBreakdowns(report.rawCandidates, report.meta.jobs);
    }, [report]);

    const perMonthData = useMemo(() => {
      if (!report?.rawCandidates?.length || !monthColumns?.length) return null;
      return computePerMonthBreakdowns(report.rawCandidates, monthColumns);
    }, [report, monthColumns]);

    const activeColumns = useMemo(() => {
      if (comparisonMode === 'month') {
        return monthColumns;
      }
      return report?.meta?.jobs || [];
    }, [comparisonMode, monthColumns, report?.meta?.jobs]);

    const activeBreakdownData = useMemo(() => {
      if (comparisonMode === 'month') {
        return perMonthData;
      }
      return perJobData;
    }, [comparisonMode, perMonthData, perJobData]);

    const isMultiColumnMode = comparisonMode === 'month' ? monthColumns.length > 0 : isMultiJob;

    /* ── Candidate view: the location tables count every applicant, or only Selected ones.
       Outcome breakdown, funnel, timeline and roster always use every applicant. ── */
    const isSelectedView = candidateView === 'selected';
    const viewCandidates = useMemo(() => {
      const all = report?.rawCandidates || [];
      return isSelectedView ? all.filter((c) => c.outcome === 'Selected') : all;
    }, [report, isSelectedView]);

    const viewPerJobData = useMemo(() => {
      if (!isSelectedView) return perJobData;
      if (!viewCandidates.length || !report?.meta?.jobs?.length) return null;
      return computePerJobBreakdowns(viewCandidates, report.meta.jobs);
    }, [isSelectedView, perJobData, viewCandidates, report]);

    const viewPerMonthData = useMemo(() => {
      if (!isSelectedView) return perMonthData;
      if (!viewCandidates.length || !monthColumns?.length) return null;
      return computePerMonthBreakdowns(viewCandidates, monthColumns);
    }, [isSelectedView, perMonthData, viewCandidates, monthColumns]);

    /* ── Single-job category data (computed from the viewed candidates) ── */
    const singleJobCategories = useMemo(() => {
      if (!viewCandidates.length || isMultiJob) return null;
      return {
        region: buildSingleJobRegionCategory(viewCandidates),
        city: buildSingleJobCategory(viewCandidates, (c) => toTitleCase(c.storeCity)),
        state: buildSingleJobCategory(viewCandidates, (c) => toTitleCase(c.storeState)),
        arm: buildSingleJobCategory(viewCandidates, (c) => c.armName ? toTitleCase(c.armName) : ''),
        store: buildSingleJobCategory(viewCandidates, (c) =>
          c.storeId ? `${c.storeId}${c.storeName ? ` — ${c.storeName}` : ''}` : ''
        ),
      };
    }, [viewCandidates, isMultiJob]);

    const categoryData = comparisonMode === 'month' ? viewPerMonthData?.categories : isMultiJob ? viewPerJobData?.categories : singleJobCategories;
    const categoryTitlePrefix = isSelectedView ? 'Selected candidates' : 'Candidates';

    const handleRefreshReport = () => {
      if (!canLoadLiveReport) return;
      reportQuery.refetch();
    };

    return (
      <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-5">
        <style>{customScrollbarCss}</style>
        {/* ── Header row ── */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-950">Reports</h1>
            <p className="mt-1 text-sm text-slate-600">Hiring analytics for a selected job and time frame · live, always current.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative" ref={actionsMenuRef}>
              <button
                type="button"
                onClick={() => setIsActionsOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={isActionsOpen}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none"
              >
                Actions
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
              {isActionsOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg z-50 text-left whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={async () => {
                      if (report) {
                        await exportExcel(report, {
                          comparisonMode,
                          monthColumns,
                          perMonthData,
                          perJobData,
                          isSelectedView,
                          categoryMonthData: viewPerMonthData,
                          categoryJobData: viewPerJobData,
                          categoryTotal: viewCandidates.length,
                        });
                      }
                      setIsActionsOpen(false);
                    }}
                    disabled={!hasLiveData}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                  >
                    <Download className="h-4 w-4 text-slate-500" />
                    Download Excel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsActionsOpen(false);
                      setIsImportModalOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    <Upload className="h-4 w-4 text-slate-500" />
                    Import
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    type="button"
                    onClick={() => {
                      setIsActionsOpen(false);
                      setIsDeleteModalOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50 whitespace-nowrap"
                  >
                    <Trash2 className="h-4 w-4 text-red-500 shrink-0" />
                    Delete External Job
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div ref={filterBarRef} className="rounded-2xl border border-slate-200 bg-white px-4 py-4 sm:px-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[330px_1fr_1fr_auto] xl:items-end">
            <div className="relative">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                Time frame
              </label>
              <button
                type="button"
                onClick={() => setIsCustomDateModalOpen((prev) => !prev)}
                className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-700 shadow-xs hover:border-blue-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 transition-all"
              >
                <div className="flex items-center gap-2 truncate">
                  <Calendar className="h-4 w-4 text-[#007BFF] shrink-0" />
                  <span className="truncate text-slate-900 font-bold">
                    {range.from && range.to
                      ? `${formatDateLabel(range.from)} – ${formatDateLabel(range.to)}`
                      : 'Select Date Range'}
                  </span>
                </div>
                <ChevronDown className={cn("h-4 w-4 text-slate-400 shrink-0 transition-transform duration-200", isCustomDateModalOpen && "rotate-180")} />
              </button>

              {/* Attached Floating Interactive Calendar Popover */}
              {isCustomDateModalOpen && (
                <DateRangePopover
                  initialRange={range}
                  onClose={() => setIsCustomDateModalOpen(false)}
                  onApply={(newRange) => {
                    setTimeFrame('custom');
                    setRange(newRange);
                    setIsCustomDateModalOpen(false);
                  }}
                />
              )}
            </div>
            <div className="relative">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                Job post{selectedJobIds.length > 0 ? ` (${selectedJobIds.length})` : ''}
              </label>
              <button
                type="button"
                onClick={() => setJobDropdownOpen((prev) => !prev)}
                className="flex h-11 w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20"
              >
                <span className="truncate">
                  {jobsLoading
                    ? 'Loading jobs...'
                    : selectedJobIds.length === 0
                      ? 'Select job posts'
                      : selectedJobIds.length === 1
                        ? jobs.find((j) => String(j._id || j.id) === selectedJobIds[0])?.title || 'Selected'
                        : `${selectedJobIds.length} jobs selected`}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
              </button>
              {jobDropdownOpen && (
                <div className="absolute left-0 top-full z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white py-1 shadow-lg max-h-60 overflow-y-auto minimal-scrollbar">
                  {jobs.length > 0 && (
                    <label className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedJobIds.length === jobs.length}
                          onChange={() => {
                            if (selectedJobIds.length === jobs.length) {
                              setSelectedJobIds([]);
                            } else {
                              setSelectedJobIds(jobs.map((j) => String(j._id || j.id)));
                            }
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-[#007BFF] focus:ring-[#007BFF]/20"
                        />
                        <span className="truncate font-semibold text-slate-900">Select All</span>
                      </div>
                    </label>
                  )}
                  {jobs.map((job) => {
                    const jid = String(job._id || job.id);
                    const checked = selectedJobIds.includes(jid);
                    return (
                      <label
                        key={jid}
                        className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setSelectedJobIds((prev) =>
                                checked ? prev.filter((id) => id !== jid) : [...prev, jid]
                              );
                            }}
                            className="h-4 w-4 rounded border-slate-300 text-[#007BFF] focus:ring-[#007BFF]/20"
                          />
                          <span className="truncate">{job.title || 'Untitled job post'}</span>
                        </div>
                        {job.isExternal && (
                          <span className="inline-flex shrink-0 items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-200/80">
                            External
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Region</label>
              <div className="grid grid-cols-3 gap-0.5 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-0.5 sm:flex">
                {[['all', 'All'], ['N', 'North'], ['S', 'South'], ['E', 'East'], ['W', 'West'], ['C', 'Central']].map(([value, label]) => (
                  <button key={value} type="button" onClick={() => setSelectedRegion(value)} className={cn('flex-1 rounded-md px-2 py-2 text-sm font-semibold transition-colors sm:px-3', selectedRegion === value ? 'bg-white text-[#007BFF] shadow-sm' : 'text-slate-600 hover:text-slate-950')}>{label}</button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleRefreshReport}
              disabled={!canLoadLiveReport}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#007BFF] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none whitespace-nowrap"
            >
              {isLoadingReport ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Generate Report
            </button>
          </div>
          {selectedJobIds.length > 0 && screenings.length > 0 && (
            <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4"><span className="text-xs text-slate-500">All screenings for selected jobs ({selectedScreeningIds.length})</span></div>
          )}
        </div>

        {/* ── Error / empty / loading state ── */}
        {!report && reportErrorMessage && !isLoadingReport && (
          <ErrorReportState
            message={reportErrorMessage}
            onRetry={handleRefreshReport}
            isRetrying={isLoadingReport}
          />
        )}
        {!report && !(reportErrorMessage && !isLoadingReport) && (
          <EmptyReportState isLoading={isLoadingReport} />
        )}

        {/* ── Report content ── */}
        {report && (
          <>
            {/* Info strip */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 text-sm text-slate-700">
                <span>Showing </span><span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-black text-[#007BFF]">{report.summary.totalCandidates} candidates</span>
                <span> · {formatDateLabel(report.meta.from)} to {formatDateLabel(report.meta.to)}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
              {/* Candidate View Selector — drives the location tables only */}
              <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white p-1 shadow-xs shrink-0">
                <span className="px-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Show:</span>
                {[['total', 'Total Candidates'], ['selected', 'Selected Candidates']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setCandidateView(value)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs font-bold transition-all',
                      candidateView === value
                        ? 'bg-[#007BFF] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Comparison Mode Selector */}
              <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white p-1 shadow-xs shrink-0">
                <span className="px-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Compare:</span>
                <button
                  type="button"
                  onClick={() => setComparisonMode('job')}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-bold transition-all',
                    comparisonMode === 'job'
                      ? 'bg-[#007BFF] text-white shadow-xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  )}
                >
                  By Job Post
                </button>
                <button
                  type="button"
                  onClick={() => setComparisonMode('month')}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-bold transition-all',
                    comparisonMode === 'month'
                      ? 'bg-[#007BFF] text-white shadow-xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  )}
                >
                  By Month
                </button>
              </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
              <OutcomePanel
                report={report}
                isMultiColumnMode={isMultiColumnMode}
                columns={activeColumns}
                breakdownOutcomes={activeBreakdownData?.outcomes}
                breakdownFunnel={activeBreakdownData?.funnel}
                activeModal={activeModal}
                setActiveModal={setActiveModal}
              />
              <FunnelPanel
                report={report}
                isMultiColumnMode={isMultiColumnMode}
                columns={activeColumns}
                breakdownFunnel={activeBreakdownData?.funnel}
                activeModal={activeModal}
                setActiveModal={setActiveModal}
              />
            </div>

            {categoryData && (
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-6 min-w-0 lg:col-span-3">
                  <CategoryPanel title={`${categoryTitlePrefix} by State`} data={categoryData.state} isMultiColumnMode={isMultiColumnMode} columns={activeColumns} panelId="state" activeModal={activeModal} setActiveModal={setActiveModal} />
                </div>
                <div className="col-span-6 min-w-0 lg:col-span-3">
                  <CategoryPanel title={`${categoryTitlePrefix} by City`} data={categoryData.city} isMultiColumnMode={isMultiColumnMode} columns={activeColumns} panelId="city" activeModal={activeModal} setActiveModal={setActiveModal} />
                </div>
                <div className="col-span-6 min-w-0 lg:col-span-3">
                  <CategoryPanel title={`${categoryTitlePrefix} by Region`} data={categoryData.region} isMultiColumnMode={isMultiColumnMode} columns={activeColumns} panelId="region" activeModal={activeModal} setActiveModal={setActiveModal} />
                </div>
                <div className="col-span-6 min-w-0 lg:col-span-3">
                  <CategoryPanel title={`${categoryTitlePrefix} per ARM`} data={categoryData.arm} isMultiColumnMode={isMultiColumnMode} columns={activeColumns} panelId="arm" />
                </div>
                <div className="col-span-6 min-w-0">
                  <CategoryPanel title={`${categoryTitlePrefix} by Store`} data={categoryData.store} isMultiColumnMode={isMultiColumnMode} columns={activeColumns} panelId="store" />
                </div>
              </div>
            )}
            {!categoryData && isSelectedView && report.rawCandidates?.length > 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
                No selected candidates in this range yet — switch to Total Candidates to see every applicant by location.
              </div>
            )}

            <TimelinePanel report={report} isMultiJob={isMultiJob} perJobData={perJobData} />
            <RosterPanel report={report} columns={activeColumns} comparisonMode={comparisonMode} />
            {activeModal && (
              <VisualModal
                activeModal={activeModal}
                onClose={() => setActiveModal(null)}
                report={report}
                isMultiColumnMode={isMultiColumnMode}
                columns={activeColumns}
                breakdownData={activeBreakdownData}
                categoryData={categoryData}
                categoryTitlePrefix={categoryTitlePrefix}
              />
            )}
          </>
        )}

        {/* Candidate XLS Import Modal */}
        {isImportModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
            <div className="relative w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                    Import Candidates (XLS / CSV)
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Upload candidate data to populate reports analytics and raw candidate roster.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(false)}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Top Info Banner & Download Sample */}
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between rounded-xl bg-blue-50/80 p-3.5 border border-blue-100 gap-3">
                <div className="text-xs text-blue-900">
                  <span className="font-semibold">Need template format?</span> Download our pre-formatted sample Excel file with expected columns.
                </div>
                <button
                  type="button"
                  onClick={handleDownloadSampleXls}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  Sample XLS Format
                </button>
              </div>

              {/* Destination Mode Choice */}
              <div className="mt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">
                  Destination Job Post
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label
                    className={`flex cursor-pointer flex-col rounded-xl border p-3 transition-all ${importMode === 'new'
                      ? 'border-blue-600 bg-blue-50/40 ring-1 ring-blue-600'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="importMode"
                        checked={importMode === 'new'}
                        onChange={() => setImportMode('new')}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs font-bold text-slate-900">New Job Post</span>
                      <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                        External Uploaded
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500 pl-5">
                      Will not show on main Job Posts list. Created for reports data only.
                    </p>
                  </label>

                  <label
                    className={`flex cursor-pointer flex-col rounded-xl border p-3 transition-all ${importMode === 'existing'
                      ? 'border-blue-600 bg-blue-50/40 ring-1 ring-blue-600'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="importMode"
                        checked={importMode === 'existing'}
                        onChange={() => setImportMode('existing')}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs font-bold text-slate-900">Existing External Job Post</span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500 pl-5">
                      Import candidates into a previously created external uploaded job post.
                    </p>
                  </label>
                </div>

                {/* Mode inputs */}
                {importMode === 'new' ? (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Job Post Name *
                    </label>
                    <input
                      type="text"
                      value={newJobTitle}
                      onChange={(e) => setNewJobTitle(e.target.value)}
                      placeholder="e.g. HDFC External Hiring Drive 2026"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Select External Job Post *
                    </label>
                    <select
                      value={selectedExistingJobId}
                      onChange={(e) => setSelectedExistingJobId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    >
                      <option value="">Select an external job post...</option>
                      {jobs
                        .filter((j) => j.isExternal === true)
                        .map((j) => (
                          <option key={j._id || j.id} value={j._id || j.id}>
                            {j.title}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>

              {/* File Upload Zone */}
              <div className="mt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">
                  Upload File (.xlsx, .xls, .csv)
                </label>
                <div className="relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 p-5 hover:bg-slate-100/60 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileSelect}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                  <Upload className="h-7 w-7 text-slate-400 mb-1" />
                  <span className="text-xs font-semibold text-slate-700">
                    {importFile ? importFile.name : 'Click or drop file here'}
                  </span>
                  <span className="text-[11px] text-slate-400 mt-0.5">
                    Supports Excel (.xlsx, .xls) and CSV format
                  </span>
                </div>
              </div>

              {/* Validation Errors Panel */}
              {validationErrors.length > 0 && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50/90 p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-xs font-bold text-red-800 mb-1.5">
                    <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                    <span>Validation Failed: Found {validationErrors.length} error(s) in your Excel file</span>
                  </div>
                  <p className="text-[11px] text-red-700 mb-2">
                    Please correct the specified row and column mistakes in your Excel file and re-upload:
                  </p>
                  <div className="max-h-44 overflow-y-auto rounded-lg border border-red-200 bg-white p-2 space-y-1.5 text-xs">
                    {validationErrors.map((err, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-slate-800">
                        <span className="font-mono font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded text-[10px] shrink-0">
                          Row {err.row} • Col "{err.column}"
                        </span>
                        <span className="text-slate-700 text-[11px] mt-0.5">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Parsing Preview / Error / Success */}
              {parsedCandidates.length > 0 && validationErrors.length === 0 && (
                <div className="mt-3 rounded-lg bg-emerald-50 p-2.5 border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
                  <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span>
                    Ready to import <strong>{parsedCandidates.length} candidate rows</strong> from <em>{importFile?.name}</em>.
                  </span>
                </div>
              )}

              {importError && validationErrors.length === 0 && (
                <div className="mt-3 rounded-lg bg-rose-50 p-2.5 border border-rose-200 text-xs text-rose-700 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
                  <span>{importError}</span>
                </div>
              )}

              {importSuccess && (
                <div className="mt-3 rounded-lg bg-emerald-50 p-2.5 border border-emerald-200 text-xs font-semibold text-emerald-800 flex items-center gap-2">
                  <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{importSuccess}</span>
                </div>
              )}

              {/* Modal Footer Actions */}
              <div className="mt-6 flex items-center justify-end gap-2.5 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(false)}
                  disabled={isImporting}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExecuteImport}
                  disabled={isImporting || !parsedCandidates.length || validationErrors.length > 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-3.5 w-3.5" />
                      Import Candidates
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete External Job Modal ── */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl transition-all border border-slate-100">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 text-red-600">
                    <Trash2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Delete External Job Post</h3>
                    <p className="text-xs text-slate-500">
                      Permanently remove external job posts and all candidate roster data.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Warning Callout */}
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-900 flex items-start gap-2.5">
                <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                <div>
                  <span className="font-bold block text-amber-950 mb-0.5">Warning: Permanent Data Deletion</span>
                  <span>
                    Deleting an external job post will permanently erase all candidates, screenings, and reports analytics associated with it. This action cannot be undone.
                  </span>
                </div>
              </div>

              {/* Select Job Dropdown */}
              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  Select External Job Post to Delete *
                </label>
                {jobs.filter((j) => j.isExternal === true).length > 0 ? (
                  <select
                    value={selectedJobToDelete}
                    onChange={(e) => setSelectedJobToDelete(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 bg-white shadow-xs font-medium"
                  >
                    <option value="">Choose an external job post...</option>
                    {jobs
                      .filter((j) => j.isExternal === true)
                      .map((j) => (
                        <option key={j._id || j.id} value={j._id || j.id}>
                          {j.title}
                        </option>
                      ))}
                  </select>
                ) : (
                  <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">
                    No external job posts found in your organization.
                  </p>
                )}
              </div>

              {deleteJobError && (
                <div className="mt-4 rounded-lg bg-rose-50 p-3 border border-rose-200 text-xs text-rose-700 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
                  <span>{deleteJobError}</span>
                </div>
              )}

              {deleteJobSuccess && (
                <div className="mt-4 rounded-lg bg-emerald-50 p-3 border border-emerald-200 text-xs font-semibold text-emerald-800 flex items-center gap-2">
                  <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{deleteJobSuccess}</span>
                </div>
              )}

              {/* Modal Actions */}
              <div className="mt-6 flex items-center justify-end gap-2.5 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isDeletingJob}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteExternalJob}
                  disabled={
                    isDeletingJob ||
                    !selectedJobToDelete ||
                    jobs.filter((j) => j.isExternal === true).length === 0
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300 transition-colors"
                >
                  {isDeletingJob ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete External Job Post
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }